/**
 * AdminWorkspace — Unified workspace shell for TDC + Ditch Careers (Phase 1A).
 *
 * Business switcher (Collective / Careers / Combined) → sub-tabs for
 * People, Candidates, Employers. Candidate + Employer create/edit modals live
 * here. Full API surface is /api/admin/workspace/**.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search, Plus, X, Users, Briefcase, Building2, ArrowRight, LayoutDashboard, Upload, Send, Bell, Trash2, AlertTriangle, FileText, DollarSign, Repeat, Calendar, UserX, Mail, CheckCircle2, ArrowUpDown, Phone, Bookmark, RotateCcw, UserRoundCheck } from "lucide-react";
import { WORKSPACE } from "@/constants/testIds";
import CandidateDetail from "./workspace/CandidateDetail";
import EmployerDetail from "./workspace/EmployerDetail";
import WorkspaceOverview from "./workspace/WorkspaceOverview";
import { StagePill } from "./workspace/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = `${API}/admin/workspace`;

const CANDIDATE_TYPES = [
  { value: "relocating", label: "Relocating (NZ → AU)" },
  { value: "domestic_au", label: "Domestic AU" },
];

const AVAILABILITY = ["Immediate", "2 weeks", "1 month", "3 months", "Negotiable"];
const ROLE_LEVELS = ["Junior", "Mid", "Senior", "Lead", "Manager", "Executive"];

/* ─────────────────────────── utilities ─────────────────────────── */
const cx = (...c) => c.filter(Boolean).join(" ");
const empty = (v) => v === undefined || v === null || String(v).trim() === "";

// Coerce any backend value (array/null/number/object) into a plain string so
// the form never crashes on legacy/imported shapes (e.g. languages stored as
// a list). Arrays join with commas; everything else stringifies safely.
const str = (v) => {
  if (v === undefined || v === null) return "";
  if (Array.isArray(v)) return v.filter((x) => x != null && String(x).trim()).join(", ");
  if (typeof v === "object") return Object.values(v).filter(Boolean).join(", ");
  return String(v);
};

const csvList = (v) =>
  str(v)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/* ─────────────────────────── shared ui ─────────────────────────── */
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select as SdSelect, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LiveBoard } from "./AdminLiveBoard";
import { EmailComposerExtras } from "./EmailComposerExtras";

function Field({ label, testId, value, onChange, type = "text", placeholder, required, hint }) {
  return (
    <div className="block">
      <Label className="block text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold mb-1">
        {label} {required && <span className="text-[#C6D21F]">*</span>}
      </Label>
      <Input
        data-testid={testId}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="border-[#D6DCE3] focus-visible:ring-[#0D2F4D] text-[#0D2F4D]"
      />
      {hint && <p className="mt-1 text-[11px] text-[#6F8FA8]">{hint}</p>}
    </div>
  );
}

function Select({ label, testId, value, onChange, options, required }) {
  return (
    <div className="block">
      <Label className="block text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold mb-1">
        {label} {required && <span className="text-[#C6D21F]">*</span>}
      </Label>
      <SdSelect value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger
          data-testid={testId}
          className="border-[#D6DCE3] focus:ring-[#0D2F4D] text-[#0D2F4D]"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) =>
            typeof o === "string"
              ? <SelectItem key={o} value={o}>{o}</SelectItem>
              : <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          )}
        </SelectContent>
      </SdSelect>
    </div>
  );
}

function TextareaField({ label, testId, value, onChange, placeholder, rows = 3 }) {
  return (
    <div className="block">
      <Label className="block text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold mb-1">{label}</Label>
      <Textarea
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="border-[#D6DCE3] focus-visible:ring-[#0D2F4D] text-[#0D2F4D] resize-y"
      />
    </div>
  );
}

function StatCard({ k, label, value, hint, badge }) {
  // Robust display: treat null/undefined/NaN as 0 (KPIs must reconcile with
  // the DB — never blank). If the backend returned an unexpected shape we
  // still show 0 rather than "—" so operators know the count is empty, not
  // "unknown".
  const n = typeof value === "number" && !Number.isNaN(value) ? value : 0;
  return (
    <Card data-testid={WORKSPACE.statCard(k)} className="border-[#E4E7EA]">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">{label}</p>
          {badge !== undefined && badge !== null && (
            <Badge
              data-testid={`${WORKSPACE.statCard(k)}-new-badge`}
              className="shrink-0 border border-[#B0BC1A] bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#C6D21F]"
            >
              New {badge}
            </Badge>
          )}
        </div>
        <p className="font-display text-3xl font-extrabold text-[#0D2F4D] mt-2" data-testid={`${WORKSPACE.statCard(k)}-value`}>{n}</p>
        {hint && <p className="text-[10px] text-[#6F8FA8] mt-1">{hint}</p>}
      </CardContent>
    </Card>
  );
}


/**
 * Pick the best CV entry from a candidate row for the row-level link cell.
 * Accepts multiple shapes the Careers app has shipped:
 *   • c.documents = [{doc_type:'cv', url, name}, ...]
 *   • c.documents = [{kind:'cv', url, ...}, ...]
 *   • c.documents = [{type:'cv'}]
 *   • no `documents` array — fall back to c.cv_url + c.cv_filename.
 * Returns { url, name } or null.
 */
function pickCvDoc(c) {
  const docs = Array.isArray(c?.documents) ? c.documents : [];
  const looksLikeCv = (d) => {
    const t = String(d?.doc_type || d?.kind || d?.type || "").toLowerCase();
    const n = String(d?.name || d?.filename || "").toLowerCase();
    return t === "cv" || t === "resume"
      || /(^|[^a-z])(cv|resume)([^a-z]|$)/i.test(n)
      || /(cv|resume)\.(pdf|docx?|rtf)$/.test(n);
  };
  const isSafeUrl = (u) => {
    if (typeof u !== "string" || !u) return false;
    // Whitelist http(s) + relative paths only — block javascript:/data:/file:
    // etc. so a hostile ingest payload can't inject a clickable exploit link.
    return /^(https?:\/\/|\/)/i.test(u.trim());
  };
  const hit = docs.find((d) => isSafeUrl(d?.url) && looksLikeCv(d));
  if (hit) return { url: hit.url, name: hit.name || hit.filename || "CV" };
  if (isSafeUrl(c?.cv_url)) return { url: c.cv_url, name: c.cv_filename || "CV" };
  return null;
}


function TagPills({ tags = [] }) {
  if (!tags.length) return <span className="text-[#6F8FA8] text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((t) => (
        <span
          key={t}
          className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#F2F2EF] text-[#0D2F4D] border border-[#E4E7EA]"
        >
          {t}
        </span>
      ))}
    </div>
  );
}

/* ─────────────────────────── candidate modal ─────────────────────────── */
function CandidateModal({ open, onClose, onSaved, onCreated, pw, editing }) {
  const [f, setF] = useState({
    email: "", first_name: "", last_name: "", phone: "",
    candidate_type: "relocating", visa_status: "", visa_type: "",
    location_now: "", location_target: "",
    industry: "", current_company: "", role_level: "", skills: "",
    years_experience: "", cv_url: "", linkedin: "",
    availability: "", salary_expectation: "",
    tags: "", notes: "",
    headline: "", summary: "", highlights_text: "",
    work_history: "", education: "", certifications: "",
  });
  const [saving, setSaving] = useState(false);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const c = editing.candidate;
      const p = editing.person || {};
      setF({
        email: str(p.email),
        first_name: str(p.first_name),
        last_name: str(p.last_name),
        phone: str(p.phone),
        candidate_type: c.candidate_type || "relocating",
        visa_status: str(c.visa_status),
        visa_type: str(c.visa_type),
        location_now: str(c.location_now),
        location_target: str(c.location_target),
        industry: str(c.industry),
        current_company: str(c.current_company),
        role_level: str(c.role_level),
        skills: str(c.skills),
        years_experience: c.years_experience ?? "",
        cv_url: str(c.cv_url),
        linkedin: str(c.linkedin),
        availability: str(c.availability),
        salary_expectation: str(c.salary_expectation),
        tags: str(p.tags),
        notes: str(c.notes),
        headline: str(c.headline),
        summary: str(c.summary),
        highlights_text: str(c.highlights_text),
        work_history: str(c.work_history),
        education: str(c.education),
        certifications: str(c.certifications),
        role_focus: str(c.role_focus),
        available_from: str(c.available_from),
        preferred_work_type: str(c.preferred_work_type),
        drivers_licence: str(c.drivers_licence),
        real_estate_registration: str(c.real_estate_registration),
        police_clearance: str(c.police_clearance),
        languages: str(c.languages),
        systems_tools: str(c.systems_tools),
      });
      setShowMore(true);   // Edit mode → expand full form
    } else {
      setF({
        email: "", first_name: "", last_name: "", phone: "",
        candidate_type: "relocating", visa_status: "", visa_type: "",
        location_now: "", location_target: "",
        industry: "", current_company: "", role_level: "", skills: "",
        years_experience: "", cv_url: "", linkedin: "",
        availability: "", salary_expectation: "",
        tags: "", notes: "",
        headline: "", summary: "", highlights_text: "",
        work_history: "", education: "", certifications: "",
        role_focus: "", available_from: "", preferred_work_type: "",
        drivers_licence: "", real_estate_registration: "", police_clearance: "",
        languages: "", systems_tools: "",
      });
      setShowMore(false);  // Fresh add → quick 4-field form
    }
  }, [open, editing]);

  const upd = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (empty(f.email) || empty(f.first_name)) {
      toast.error("Email and first name are required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) {
      toast.error("Enter a valid email.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        email: f.email.trim(),
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        phone: f.phone.trim(),
        candidate_type: f.candidate_type,
        visa_status: f.visa_status.trim(),
        visa_type: f.visa_type.trim(),
        location_now: f.location_now.trim(),
        location_target: f.location_target.trim(),
        industry: f.industry.trim(),
        current_company: f.current_company.trim(),
        role_level: f.role_level,
        skills: csvList(f.skills),
        years_experience: empty(f.years_experience) ? null : Number(f.years_experience),
        cv_url: f.cv_url.trim(),
        linkedin: f.linkedin.trim(),
        availability: f.availability,
        salary_expectation: f.salary_expectation.trim(),
        tags: csvList(f.tags),
        notes: f.notes,
        headline: f.headline.trim(),
        summary: f.summary,
        highlights_text: f.highlights_text,
        work_history: f.work_history,
        education: f.education,
        certifications: csvList(f.certifications),
        role_focus: f.role_focus.trim(),
        available_from: f.available_from.trim(),
        preferred_work_type: f.preferred_work_type.trim(),
        drivers_licence: f.drivers_licence.trim(),
        real_estate_registration: f.real_estate_registration.trim(),
        police_clearance: f.police_clearance.trim(),
        languages: f.languages.trim(),
        systems_tools: f.systems_tools.trim(),
      };
      const r = editing
        ? await axios.patch(`${BASE}/candidates/${editing.id}`, body, {
            headers: { "X-Admin-Password": pw },
          })
        : await axios.post(`${BASE}/candidates`, body, {
            headers: { "X-Admin-Password": pw },
          });
      const newId = r?.data?.candidate?.id || editing?.id;
      const mergedExisting = !!r?.data?.merged_existing;
      const msg = editing
        ? "Candidate updated."
        : mergedExisting
          ? "Merged into existing Careers profile. Opening…"
          : "Candidate created. Opening profile…";
      toast.success(msg);
      onSaved?.();
      onClose();
      // Always navigate to the profile — whether newly-created OR merged into
      // an existing Careers profile (e.g. from Talent Hub / SEEK).
      if (newId) onCreated?.(newId);
    } catch (e) {
      // Surface the real reason — the generic toast hid CORS / 401 / 422
      // details on production. Priya's screenshots now show exactly what
      // the backend / browser rejected.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const detailStr = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(" · ")
          : (detail && JSON.stringify(detail)) || "";
      const msg = status
        ? `Couldn't save candidate (${status}). ${detailStr || e.message || ""}`.trim()
        : `Couldn't save candidate — ${e.message || "network / CORS error"}`;
      toast.error(msg);
      // Also log the full error object for DevTools inspection.
      console.error("[candidate save failed]", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="candidate-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold text-[#0D2F4D]">
            {editing ? "Edit candidate" : "Add candidate"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Core fields — always visible */}
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="First name" required testId={WORKSPACE.formFirstName} value={f.first_name} onChange={upd("first_name")} placeholder="Jane" />
            <Field label="Last name" testId={WORKSPACE.formLastName} value={f.last_name} onChange={upd("last_name")} placeholder="Doe" />
            <Field label="Email" required testId={WORKSPACE.formEmail} value={f.email} onChange={upd("email")} placeholder="jane@example.com" />
            <Field label="Phone" testId={WORKSPACE.formPhone} value={f.phone} onChange={upd("phone")} placeholder="+64 21 000 000" />
            <Field label="Company (current employer)" testId="cand-current-company" value={f.current_company} onChange={upd("current_company")} placeholder="Kiwi Widgets Ltd" hint="Optional — where they work today" />
            <Select
              label="Candidate type"
              required
              testId={WORKSPACE.formCandidateType}
              value={f.candidate_type}
              onChange={upd("candidate_type")}
              options={CANDIDATE_TYPES}
            />
          </div>

          {/* Optional expandable section */}
          {!showMore ? (
            <button
              type="button"
              onClick={() => setShowMore(true)}
              data-testid="cand-show-more"
              className="text-xs font-bold uppercase tracking-widest text-[#0D2F4D] hover:text-[#0D2F4D]/70 inline-flex items-center gap-1"
            >
              <Plus size={12}/> More details (optional)
            </button>
          ) : (
            <div className="space-y-4 pt-2 border-t border-[#E4E7EA]">
              <p className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold">More details</p>
              <div className="grid md:grid-cols-2 gap-4">
                {f.candidate_type === "relocating" && (
                  <Field label="Visa status" testId={WORKSPACE.formVisaStatus} value={f.visa_status} onChange={upd("visa_status")} placeholder="e.g. Approved / pending / TBC" />
                )}
                <Field label="Location now" testId={WORKSPACE.formLocationNow} value={f.location_now} onChange={upd("location_now")} placeholder="Auckland" />
                <Field label="Location target" testId={WORKSPACE.formLocationTarget} value={f.location_target} onChange={upd("location_target")} placeholder="Brisbane" />
                <Field label="Industry" testId={WORKSPACE.formIndustry} value={f.industry} onChange={upd("industry")} placeholder="Software / Trades / Finance" />
                <Select label="Role level" value={f.role_level} onChange={upd("role_level")} options={ROLE_LEVELS} />
                <Field label="Skills" testId={WORKSPACE.formSkills} value={f.skills} onChange={upd("skills")} placeholder="Comma-separated" hint="e.g. React, FastAPI, MongoDB" />
                <Field label="Years experience" testId={WORKSPACE.formYearsExperience} value={f.years_experience} onChange={upd("years_experience")} type="number" placeholder="8" />
                <Select label="Availability" value={f.availability} onChange={upd("availability")} options={AVAILABILITY} />
                <Field label="Salary expectation" value={f.salary_expectation} onChange={upd("salary_expectation")} placeholder="$140k AUD" />
                <Field label="LinkedIn" value={f.linkedin} onChange={upd("linkedin")} placeholder="https://linkedin.com/in/…" />
                <Field label="CV URL" value={f.cv_url} onChange={upd("cv_url")} placeholder="https://…" />
              </div>
              <Field label="Tags" testId={WORKSPACE.formTags} value={f.tags} onChange={upd("tags")} placeholder="Comma-separated" hint="e.g. priority, dev, referred-by-jane" />
              <TextareaField label="Notes" testId={WORKSPACE.formNotes} value={f.notes} onChange={upd("notes")} rows={4} />

              {/* ── Ditch Careers Candidate Profile (recruiter-only) ── */}
              <div className="pt-4 border-t border-[#E4E7EA] space-y-3">
                <div className="flex items-center gap-2">
                  <p className="text-[10px] uppercase tracking-widest text-[#0D2F4D] font-bold">Ditch Careers Candidate Profile</p>
                  <span className="text-[9px] text-[#6F8FA8] italic">Powers the Candidate Profile PDF / preview. Empty fields show as — in the document.</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Role focus" value={f.role_focus} onChange={upd("role_focus")} placeholder="Property Manager | Leasing Consultant | BDM" hint="Separate with | — shown under the name" testId="cand-profile-role-focus" />
                  <Field label="Available from" value={f.available_from} onChange={upd("available_from")} placeholder="1 July 2026" testId="cand-profile-available-from" />
                  <Field label="Preferred work type" value={f.preferred_work_type} onChange={upd("preferred_work_type")} placeholder="Full-time, Permanent" testId="cand-profile-work-type" />
                  <Field label="Driver's licence" value={f.drivers_licence} onChange={upd("drivers_licence")} placeholder="Full licence + own vehicle" testId="cand-profile-licence" />
                  <Field label="Real estate registration" value={f.real_estate_registration} onChange={upd("real_estate_registration")} placeholder="QLD Registration Certificate (2024)" testId="cand-profile-rea-rego" />
                  <Field label="Police clearance" value={f.police_clearance} onChange={upd("police_clearance")} placeholder="Current — issued Mar 2026" testId="cand-profile-police" />
                  <Field label="Languages" value={f.languages} onChange={upd("languages")} placeholder="English (native), Te Reo Māori (conversational)" testId="cand-profile-languages" />
                  <Field label="Systems & technology" value={f.systems_tools} onChange={upd("systems_tools")} placeholder="Property Tree, REST, VaultRE, Xero" hint="Comma-separated" testId="cand-profile-systems" />
                </div>
                <Field label="Headline" value={f.headline} onChange={upd("headline")} placeholder="Senior Full-Stack · 8 yrs · Fintech" testId="cand-cv-headline" />
                <TextareaField label="Profile summary" value={f.summary} onChange={upd("summary")} rows={3} testId="cand-cv-summary" placeholder="2-3 sentence career profile. Blank line = new paragraph." />
                <TextareaField label="Key highlights (one per line)" value={f.highlights_text} onChange={upd("highlights_text")} rows={4} testId="cand-cv-highlights" placeholder={"Cut inference costs 41% via caching\nLed 6-person platform squad\nDrove Series-A tech due diligence"} />
                <TextareaField label="Experience / work history" value={f.work_history} onChange={upd("work_history")} rows={6} testId="cand-cv-work" placeholder={"**Company Ltd** — Role (dates)\nOne-line summary of scope.\n\n**Previous Co** — Role (dates)\nAnother one-liner."} />
                <TextareaField label="Education" value={f.education} onChange={upd("education")} rows={3} testId="cand-cv-education" placeholder={"**BSc Computer Science, University of Auckland (2016)**\n\n**AWS CSA — Associate (2023)**"} />
                <Field label="Certifications" value={f.certifications} onChange={upd("certifications")} placeholder="Comma-separated" hint="e.g. AWS CSA, GCP Data Engineer" testId="cand-cv-certs" />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="pt-4">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid={WORKSPACE.formCancel}
          >
            Cancel
          </Button>
          <Button
            data-testid={WORKSPACE.formSubmit}
            disabled={saving}
            onClick={submit}
            className="bg-[#0D2F4D] text-white hover:bg-[#1a4063]"
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Create candidate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────── employer modal ─────────────────────────── */
function EmployerModal({ open, onClose, onSaved, onCreated, pw, editing, team = [], defaultStatus = "" }) {
  const [f, setF] = useState({
    company_name: "", company_domain: "",
    contact_email: "", contact_first_name: "", contact_last_name: "", contact_phone: "",
    industry: "", location: "",
    area: "", assigned_to: "", source: "", owner: "",
    hiring_for: "", tags: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const e = editing.employer;
      const c = editing.company || {};
      const p = editing.contact || {};
      setF({
        company_name: str(c.name),
        company_domain: str(c.domain),
        contact_email: str(p.email),
        contact_first_name: str(p.first_name),
        contact_last_name: str(p.last_name),
        contact_phone: str(p.phone),
        industry: str(e.industry),
        location: str(e.location),
        area: str(e.area),
        assigned_to: str(e.assigned_to),
        source: str(e.source),
        owner: str(e.owner),
        hiring_for: str(e.hiring_for),
        tags: str(c.tags),
        notes: str(e.notes),
      });
      setShowMore(true);
    } else {
      setF({
        company_name: "", company_domain: "",
        contact_email: "", contact_first_name: "", contact_last_name: "", contact_phone: "",
        industry: "", location: "",
        area: "", assigned_to: "", source: "", owner: "",
        hiring_for: "", tags: "", notes: "",
      });
      setShowMore(false);
    }
  }, [open, editing]);

  const upd = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (empty(f.company_name) || empty(f.contact_email)) {
      toast.error("Company name and contact email are required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contact_email.trim())) {
      toast.error("Enter a valid contact email.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        company_name: f.company_name.trim(),
        company_domain: f.company_domain.trim().toLowerCase(),
        contact_email: f.contact_email.trim(),
        contact_first_name: f.contact_first_name.trim(),
        contact_last_name: f.contact_last_name.trim(),
        contact_phone: f.contact_phone.trim(),
        industry: f.industry.trim(),
        location: f.location.trim(),
        area: f.area.trim(),
        assigned_to: f.assigned_to.trim().toLowerCase(),
        source: f.source.trim(),
        owner: f.owner.trim().toLowerCase(),
        hiring_for: csvList(f.hiring_for),
        tags: csvList(f.tags),
        notes: f.notes,
      };
      // Sep 2026: if the recruiter is on the "Active Employers" view,
      // land the new employer as active_client so it doesn't visibly
      // disappear from their filtered list into Outreach Targets.
      if (!editing && defaultStatus) {
        body.recruitment_status = defaultStatus;
      }
      const r = editing
        ? await axios.patch(`${BASE}/employers/${editing.id}`, body, {
            headers: { "X-Admin-Password": pw },
          })
        : await axios.post(`${BASE}/employers`, body, {
            headers: { "X-Admin-Password": pw },
          });
      const newId = r?.data?.employer?.id || editing?.id;
      const mergedExisting = !!r?.data?.merged_existing;
      const msg = editing
        ? "Employer updated."
        : mergedExisting
          ? "Merged into existing Careers employer. Opening…"
          : "Employer created. Opening profile…";
      toast.success(msg);
      onSaved?.();
      onClose();
      if (newId) onCreated?.(newId);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const detailStr = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(" · ")
          : (detail && JSON.stringify(detail)) || "";
      const msg = status
        ? `Couldn't save employer (${status}). ${detailStr || e.message || ""}`.trim()
        : `Couldn't save employer — ${e.message || "network / CORS error"}`;
      toast.error(msg);
      console.error("[employer save failed]", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="employer-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold text-[#0D2F4D]">
            {editing ? "Edit employer" : "Add employer"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Core fields */}
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Company name" required testId={WORKSPACE.formCompanyName} value={f.company_name} onChange={upd("company_name")} placeholder="Acme Pty Ltd" />
            <Field label="Contact first name" testId={WORKSPACE.formContactFirstName} value={f.contact_first_name} onChange={upd("contact_first_name")} placeholder="Jamie" />
            <Field label="Contact email" required testId={WORKSPACE.formContactEmail} value={f.contact_email} onChange={upd("contact_email")} placeholder="jamie@acme.com.au" />
            <Field label="Contact phone" testId="empl-phone" value={f.contact_phone} onChange={upd("contact_phone")} placeholder="+61 3 000 0000" />
          </div>

          {!showMore ? (
            <button
              type="button"
              onClick={() => setShowMore(true)}
              data-testid="empl-show-more"
              className="text-xs font-bold uppercase tracking-widest text-[#0D2F4D] hover:text-[#0D2F4D]/70 inline-flex items-center gap-1"
            >
              <Plus size={12}/> More details (optional)
            </button>
          ) : (
            <div className="space-y-4 pt-2 border-t border-[#E4E7EA]">
              <p className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold">More details</p>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Company domain" testId={WORKSPACE.formCompanyDomain} value={f.company_domain} onChange={upd("company_domain")} placeholder="acme.com.au" />
                <Field label="Contact last name" value={f.contact_last_name} onChange={upd("contact_last_name")} />
                <Field label="Industry" value={f.industry} onChange={upd("industry")} placeholder="Software / Hospitality / Construction" />
                <Field label="Location" value={f.location} onChange={upd("location")} placeholder="Brisbane" />
              </div>

              {/* ── Hunter fields ── */}
              <div className="pt-3 border-t border-[#E4E7EA] space-y-4">
                <p className="text-[10px] uppercase tracking-widest text-[#0D2F4D] font-bold">Sales Hunter</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Area / region" testId="empl-area" value={f.area} onChange={upd("area")} placeholder="Melbourne CBD" hint="Coarser than location — used for the Hunter map filter" />
                  <Field label="Source" testId="empl-source" value={f.source} onChange={upd("source")} placeholder="linkedin / referral / cold-email" />
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold mb-1 block">Assigned To</label>
                    <SdSelect value={f.assigned_to || "__none__"} onValueChange={(v) => upd("assigned_to")(v === "__none__" ? "" : v)}>
                      <SelectTrigger data-testid="empl-assigned-to" className="border-[#D6DCE3] focus:ring-[#0D2F4D] text-[#0D2F4D]"><SelectValue placeholder="Unassigned"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Unassigned</SelectItem>
                        {(team || []).map(t => <SelectItem key={t.email} value={t.email}>{t.name} — {t.email}</SelectItem>)}
                      </SelectContent>
                    </SdSelect>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold mb-1 block">Owner</label>
                    <SdSelect value={f.owner || "__none__"} onValueChange={(v) => upd("owner")(v === "__none__" ? "" : v)}>
                      <SelectTrigger data-testid="empl-owner" className="border-[#D6DCE3] focus:ring-[#0D2F4D] text-[#0D2F4D]"><SelectValue placeholder="No owner"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No owner</SelectItem>
                        {(team || []).map(t => <SelectItem key={t.email} value={t.email}>{t.name} — {t.email}</SelectItem>)}
                      </SelectContent>
                    </SdSelect>
                  </div>
                </div>
              </div>

              <Field label="Roles they're hiring for" testId={WORKSPACE.formHiringFor} value={f.hiring_for} onChange={upd("hiring_for")} placeholder="Comma-separated" hint="e.g. Senior Full-Stack, DevOps Lead" />
              <Field label="Tags" value={f.tags} onChange={upd("tags")} placeholder="priority, urgent" />
              <TextareaField label="Notes" value={f.notes} onChange={upd("notes")} rows={4} />
            </div>
          )}
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={onClose} data-testid="empl-cancel">Cancel</Button>
          <Button
            data-testid={WORKSPACE.formSubmit}
            disabled={saving}
            onClick={submit}
            className="bg-[#0D2F4D] text-white hover:bg-[#1a4063]"
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Create employer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─────────────────────────── main tab ─────────────────────────── */
export function WorkspaceTab({ pw, user, lockedSubTab = null, hideBusinessSwitcher = false, employerStatusFilter = null, initialEmployerId = null }) {
  const [business, setBusiness] = useState("careers"); // start on careers since that's the current bottleneck
  const [subTab, setSubTab] = useState(lockedSubTab || "overview");
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [abandoned, setAbandoned] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [areaFilter, setAreaFilter] = useState("");
  const [team, setTeam] = useState([]);
  const [people, setPeople] = useState([]);
  const [payingOnly, setPayingOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [sodingClientFilter, setSodingClientFilter] = useState(false);
  const [candidateActiveFilter, setCandidateActiveFilter] = useState("active");
  const [candidateSort, setCandidateSort] = useState("newest");
  const [candidatePresets, setCandidatePresets] = useState([]);
  const [candidatePresetName, setCandidatePresetName] = useState("");
  const [candidateModal, setCandidateModal] = useState({ open: false, editing: null });
  const [employerModal, setEmployerModal] = useState({ open: false, editing: null });
  const [employerImportOpen, setEmployerImportOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailReload, setDetailReload] = useState(0);
  // ── URL is the single source of truth for the open detail view (Jun 2026).
  // Deriving the ids straight from the query string (instead of mirroring
  // them into local state) makes candidate/employer detail views fully
  // deep-linkable — open in a new tab, duplicate the tab, and the browser
  // Back/Forward buttons all land on the exact record — with no state⇄URL
  // race (browser Back reliably closes the detail).
  const selectedCandidateId = searchParams.get("candidate") || null;
  const selectedEmployerId = searchParams.get("employer") || null;
  const setSelectedCandidateId = useCallback((id) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (id) { p.set("candidate", id); p.delete("employer"); } else { p.delete("candidate"); }
      return p;
    });
  }, [setSearchParams]);
  const setSelectedEmployerId = useCallback((id) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (id) { p.set("employer", id); p.delete("candidate"); } else { p.delete("employer"); }
      return p;
    });
  }, [setSearchParams]);
  // Deep-link from the Live Board: open a specific employer when the caller
  // passes an id (pushes it into the URL so it's shareable / back-able).
  useEffect(() => {
    if (initialEmployerId) setSelectedEmployerId(initialEmployerId);
  }, [initialEmployerId, setSelectedEmployerId]);
  // Gmail health banner — polls /admin/gmail/status once + every 5 min so
  // recruiters get a loud warning the moment their OAuth token expires
  // and outbound Gmail sends stop routing through their inbox.
  const [gmailStatus, setGmailStatus] = useState(null);
  const [gmailDismissed, setGmailDismissed] = useState(false);

  const H = useMemo(() => ({ headers: { "X-Admin-Password": pw } }), [pw]);
  const candidatePresetKey = useMemo(
    () => `ditch_candidate_view_presets:${user?.email || "admin"}`,
    [user?.email],
  );

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(candidatePresetKey) || "[]");
      setCandidatePresets(Array.isArray(saved) ? saved : []);
    } catch (_) {
      setCandidatePresets([]);
    }
  }, [candidatePresetKey]);

  const saveCandidatePreset = () => {
    const name = candidatePresetName.trim();
    if (!name) { toast.error("Give the preset a name first."); return; }
    const preset = {
      name, search, typeFilter, lifecycleFilter, accountFilter, sodingClientFilter,
      activeFilter: candidateActiveFilter, sort: candidateSort,
    };
    const next = [...candidatePresets.filter((p) => p.name !== name), preset];
    localStorage.setItem(candidatePresetKey, JSON.stringify(next));
    setCandidatePresets(next);
    setCandidatePresetName("");
    toast.success(`Saved view “${name}”.`);
  };

  const applyCandidatePreset = (preset) => {
    setSearch(preset.search || "");
    setTypeFilter(preset.typeFilter || "");
    setLifecycleFilter(preset.lifecycleFilter || "");
    setAccountFilter(preset.accountFilter || "");
    setSodingClientFilter(!!preset.sodingClientFilter);
    setCandidateActiveFilter(preset.activeFilter || "active");
    setCandidateSort(preset.sort || "newest");
  };

  const deleteCandidatePreset = (name) => {
    const next = candidatePresets.filter((p) => p.name !== name);
    localStorage.setItem(candidatePresetKey, JSON.stringify(next));
    setCandidatePresets(next);
  };

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE}/stats`, H);
      setStats(r.data);
    } catch (e) {
      // silent
    }
  }, [H]);

  const loadGmailStatus = useCallback(async () => {
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/gmail/status`,
        { headers: { "X-Admin-Password": pw, "X-Admin-Email": user?.email || "" } },
      );
      setGmailStatus(r.data);
    } catch (_) {
      // Silent — banner just stays hidden if the endpoint is unreachable.
    }
  }, [pw, user?.email]);

  useEffect(() => {
    loadGmailStatus();
    const t = setInterval(loadGmailStatus, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [loadGmailStatus]);

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set("candidate_type", typeFilter);
      if (lifecycleFilter) params.set("lifecycle_status", lifecycleFilter);
      if (accountFilter) params.set("account", accountFilter);
      if (sodingClientFilter) params.set("soding_client", "true");
      params.set("active", candidateActiveFilter);
      if (search) params.set("search", search);
      params.set("sort", candidateSort);
      const r = await axios.get(`${BASE}/candidates?${params.toString()}`, H);
      setCandidates(r.data.candidates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load candidates");
    } finally {
      setLoading(false);
    }
  }, [H, search, typeFilter, lifecycleFilter, accountFilter, sodingClientFilter, candidateActiveFilter, candidateSort]);

  const loadAbandoned = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/candidates/abandoned?include_nudged=true&limit=500`, H);
      setAbandoned(r.data.candidates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load abandoned signups");
    } finally {
      setLoading(false);
    }
  }, [H]);

  const loadEmployers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (areaFilter) params.set("area", areaFilter);
      if (employerStatusFilter) params.set("status", employerStatusFilter);
      const r = await axios.get(`${BASE}/employers?${params.toString()}`, H);
      setEmployers(r.data.employers || []);
      // refresh distinct area chips + team on every employer load — cheap aggregates
      axios.get(`${BASE}/employer-areas`, H).then(r => setAreas(r.data.areas || [])).catch(() => {});
      axios.get(`${BASE}/team`, H).then(r => setTeam(r.data.team || [])).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load employers");
    } finally {
      setLoading(false);
    }
  }, [H, search, areaFilter, employerStatusFilter]);

  const loadPeople = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (business !== "combined") params.set("business", business);
      if (search) params.set("search", search);
      if (payingOnly) params.set("paying_only", "true");
      const r = await axios.get(`${BASE}/people?${params.toString()}`, H);
      setPeople(r.data.people || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load people");
    } finally {
      setLoading(false);
    }
  }, [H, business, search, payingOnly]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (subTab === "candidates") loadCandidates();
    else if (subTab === "abandoned") loadAbandoned();
    else if (subTab === "employers") loadEmployers();
    else if (subTab === "people") loadPeople();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab, loadCandidates, loadAbandoned, loadEmployers, loadPeople]);

  const refresh = () => {
    loadStats();
    if (subTab === "candidates") loadCandidates();
    else if (subTab === "abandoned") loadAbandoned();
    else if (subTab === "employers") loadEmployers();
    else if (subTab === "people") loadPeople();
  };

  // when business changes, snap sub-tab to a sensible default
  useEffect(() => {
    if (business === "collective" && ["candidates", "employers"].includes(subTab)) {
      setSubTab("overview");
    }
    if (business === "careers" && subTab === "people") {
      setSubTab("overview");
    }
  }, [business]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid={WORKSPACE.root}>
      {/* Gmail-health banner (Feb 2026): loudly surfaces expired OAuth
          tokens so recruiters reconnect BEFORE their next send silently
          drops through the fallback SMTP path. Auto-hides when the
          connection is fresh; can be dismissed for this session. */}
      {gmailStatus && gmailStatus.connected && gmailStatus.last_error && !gmailDismissed && (
        <div
          data-testid="gmail-token-warning-banner"
          className="mb-4 flex items-start gap-3 rounded-2xl border border-red-300 bg-red-50 p-4 text-red-900"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-bold text-sm">
              Your Gmail connection needs attention
            </p>
            <p className="text-xs mt-1 text-red-800">
              {gmailStatus.last_error === "gmail_token_expired" || /token/i.test(gmailStatus.last_error)
                ? "Your Google token has expired — outbound Gmail sends will fall back to SMTP until you reconnect."
                : `Last error: ${gmailStatus.last_error}`}
            </p>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/oauth/gmail/authorize?admin_email=${encodeURIComponent(user?.email || "")}`}
              className="inline-block mt-2 text-xs font-bold underline hover:text-red-700"
              data-testid="gmail-reconnect-link"
            >
              Reconnect Gmail →
            </a>
          </div>
          <button
            onClick={() => setGmailDismissed(true)}
            data-testid="gmail-warning-dismiss"
            className="text-xs font-bold text-red-700 hover:text-red-900 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Business switcher — hidden on locked pages where the parent nav already implies the business context */}
      {!hideBusinessSwitcher && (
      <div className="flex items-center gap-2 mb-6">
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] mr-2">
          Business:
        </span>
        {[
          ["collective", "Collective"],
          ["careers", "Careers"],
          ["combined", "Combined"],
        ].map(([v, label]) => (
          <button
            key={v}
            data-testid={WORKSPACE.businessSwitch(v)}
            onClick={() => setBusiness(v)}
            className={cx(
              "px-4 py-1.5 rounded-full text-xs font-bold border transition-colors",
              business === v
                ? "bg-[#0D2F4D] text-white border-[#0D2F4D]"
                : "bg-white text-[#0D2F4D] border-[#D6DCE3] hover:border-[#0D2F4D]"
            )}
          >
            {label}
          </button>
        ))}
      </div>
      )}

      {/* Stats — reconciled against DB on every load, no caching. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard k="people" label="People" value={stats?.people_total} />
        <StatCard k="candidates" label="Candidates" value={stats?.candidates?.total} />
        <StatCard
          k="relocating"
          label="Relocating"
          value={stats?.relocation_leads?.unsettled}
          hint="Leads not yet settled"
        />
        <StatCard
          k="employers-active"
          label="Active employers"
          value={stats?.employers?.active}
          badge={stats?.employers?.new_24h ?? 0}
          hint="New employer leads in the last 24 hours"
        />
      </div>

      {/* Sub-tabs — hidden when the parent Admin page has already locked us to a specific view */}
      {!lockedSubTab && (
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          ["overview", "Overview", LayoutDashboard],
          business !== "collective" && ["my-day", "My Day", Bell],
          business !== "collective" && ["candidates", "Candidates", Briefcase],
          business !== "collective" && ["abandoned", "Abandoned Signups", UserX],
          business !== "collective" && ["employers", "Employers", Building2],
          ["people", "People", Users],
        ]
          .filter(Boolean)
          .map(([k, label, Icon]) => (
            <button
              key={k}
              data-testid={WORKSPACE.subTab(k)}
              onClick={() => {
                setSubTab(k);
                setSelectedCandidateId(null);
                setSelectedEmployerId(null);
              }}
              className={cx(
                "px-4 py-1.5 rounded-full text-xs font-bold border inline-flex items-center gap-1.5",
                subTab === k
                  ? "bg-[#C6D21F] text-[#0D2F4D] border-[#C6D21F]"
                  : "bg-white text-[#0D2F4D] border-[#D6DCE3] hover:border-[#0D2F4D]"
              )}
            >
              <Icon size={12} /> {label}
            </button>
          ))}
      </div>
      )}

      {/* Locked-view page header — only rendered when the parent Admin page
          has locked us onto a single sub-view (Outreach Targets / Active
          Employers). Keeps the user oriented without a full breadcrumb. */}
      {lockedSubTab === "employers" && !selectedEmployerId && (
        <div className="mb-6" data-testid={`employers-page-header-${employerStatusFilter || 'all'}`}>
          {employerStatusFilter === "active_client" ? (
            <>
              <h2 className="font-display text-3xl font-extrabold text-[#0D2F4D]">Active Employers</h2>
              <p className="text-sm text-[#6F8FA8] mt-1">Companies currently signed on as paid partners. Promote from Outreach Targets once they convert.</p>
            </>
          ) : employerStatusFilter === "prospect" ? (
            <>
              <h2 className="font-display text-3xl font-extrabold text-[#0D2F4D]">Outreach Targets</h2>
              <p className="text-sm text-[#6F8FA8] mt-1">First-contact list. Import a CSV, hunt state-by-state, promote them to Active once they sign on.</p>
            </>
          ) : (
            <>
              <h2 className="font-display text-3xl font-extrabold text-[#0D2F4D]">Employers</h2>
              <p className="text-sm text-[#6F8FA8] mt-1">Every company in the CRM — prospects, active partners, and dormant contacts.</p>
            </>
          )}
        </div>
      )}
      {lockedSubTab === "candidates" && !selectedCandidateId && (
        <div className="mb-6 flex flex-col sm:flex-row sm:items-start justify-between gap-4" data-testid="candidates-page-header">
          <div>
            <h2 className="font-display text-3xl font-extrabold text-[#0D2F4D]">Candidates</h2>
            <p className="text-sm text-[#6F8FA8] mt-1">Unified job-seeker list — new signups and relocated clients we're placing.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href="/admin?tab=candidates&view=shortlist"
              data-testid="ditch-shortlist-tab"
              className="inline-flex items-center gap-2 rounded-full bg-[#0D2F4D] px-4 py-2 text-xs font-black text-[#C6D21F] hover:bg-[#14426B]"
            >
              <UserRoundCheck size={14}/> The Ditch Shortlist
            </a>
            <AuditStageListsBtn pw={pw} onDone={() => refresh()} />
            <ActivateEmailedBtn pw={pw} onDone={() => refresh()} />
            <BackfillNotesExtract pw={pw} onDone={() => refresh()} />
            <CandidateBulkImport pw={pw} onDone={() => refresh()} />
          </div>
        </div>
      )}

      {/* Controls */}
      {!(selectedCandidateId || selectedEmployerId) && subTab !== "overview" && subTab !== "abandoned" && (
        <div className="bg-white rounded-2xl p-4 border border-[#E4E7EA] mb-6 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6F8FA8]" />
            <input
              data-testid={WORKSPACE.search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${subTab}…`}
              className="w-full text-sm rounded-full border border-[#D6DCE3] bg-white pl-8 pr-4 py-2 text-[#0D2F4D] focus:outline-none focus:border-[#0D2F4D]"
            />
          </div>
          {subTab === "candidates" && (
            <select
              data-testid={WORKSPACE.filterCandidateType}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="text-sm rounded-full border border-[#D6DCE3] bg-white px-3 py-2 text-[#0D2F4D]"
            >
              <option value="">All types</option>
              <option value="relocating">Relocating</option>
              <option value="domestic_au">Domestic AU</option>
            </select>
          )}
          {subTab === "candidates" && (
            <select
              data-testid="candidate-active-filter"
              value={candidateActiveFilter}
              onChange={(e) => setCandidateActiveFilter(e.target.value)}
              className="text-sm rounded-full border border-[#D6DCE3] bg-white px-3 py-2 text-[#0D2F4D]"
            >
              <option value="active">Active records</option>
              <option value="inactive">Inactive records</option>
              <option value="all">All records</option>
            </select>
          )}
          {subTab === "candidates" && (
            <div className="flex items-center gap-1.5" data-testid="candidate-lifecycle-filter">
              {[
                { v: "", label: "All", color: "" },
                { v: "Active", label: "Active", color: "#C6D21F" },
                { v: "Keep Warm", label: "Keep Warm", color: "#F59E0B" },
                { v: "Not Progressing", label: "Not Progressing", color: "#94A3B8" },
              ].map((s) => {
                const active = lifecycleFilter === s.v;
                return (
                  <button
                    key={s.v || "all"}
                    data-testid={`lifecycle-chip-${s.v ? s.v.toLowerCase().replace(/ /g, "-") : "all"}`}
                    onClick={() => setLifecycleFilter(s.v)}
                    className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border transition-all ${
                      active
                        ? "text-white shadow-inner"
                        : "text-[#6F8FA8] border-[#D6DCE3] hover:text-[#0D2F4D] hover:border-[#0D2F4D] bg-white"
                    }`}
                    style={active && s.color ? { background: s.color, borderColor: s.color } : {}}
                    title={s.v ? `Filter to ${s.label}` : "Show all lifecycles"}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          )}
          {subTab === "candidates" && (
            <div className="flex items-center gap-1.5" data-testid="candidate-account-filter">
              {[
                { v: "", label: "All accounts" },
                { v: "no", label: "No account yet" },
                { v: "yes", label: "Registered" },
              ].map((s) => {
                const active = accountFilter === s.v;
                return (
                  <button
                    key={s.v || "all-acct"}
                    data-testid={`account-chip-${s.v || "all"}`}
                    onClick={() => setAccountFilter(s.v)}
                    className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border transition-all ${
                      active
                        ? (s.v === "no" ? "bg-amber-500 border-amber-500 text-white" : "bg-[#0D2F4D] border-[#0D2F4D] text-white")
                        : "text-[#6F8FA8] border-[#D6DCE3] hover:text-[#0D2F4D] hover:border-[#0D2F4D] bg-white"
                    }`}
                    title={s.v === "no" ? "Uploaded candidates who haven't created a SEEK account yet" : s.label}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          )}
          {subTab === "candidates" && (
            <button
              type="button"
              data-testid="candidate-soding-client-filter"
              aria-label="Show Soding Client candidates only"
              aria-pressed={sodingClientFilter}
              onClick={() => setSodingClientFilter((value) => !value)}
              title="Show Soding Client candidates only"
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-black transition-[background-color,border-color,color,transform] hover:-translate-y-0.5 ${
                sodingClientFilter
                  ? "border-[#0D2F4D] bg-[#0D2F4D] text-[#C6D21F]"
                  : "border-[#D6DCE3] bg-white text-[#6F8FA8] hover:border-[#0D2F4D] hover:text-[#0D2F4D]"
              }`}
            >
              S
            </button>
          )}
          {subTab === "candidates" && (
            <button
              data-testid="candidate-sort-toggle"
              onClick={() => setCandidateSort((s) => (s === "oldest" ? "newest" : "oldest"))}
              className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#D6DCE3] bg-white text-[#0D2F4D] hover:border-[#0D2F4D] inline-flex items-center gap-1.5 transition-all"
              title="Flip the list between oldest-first and newest-first"
            >
              <ArrowUpDown size={12} />
              {candidateSort === "oldest" ? "Oldest first" : "Newest first"}
            </button>
          )}
          {subTab === "candidates" && (
            <button
              data-testid={WORKSPACE.createCandidateBtn}
              onClick={() => setCandidateModal({ open: true, editing: null })}
              className="ml-auto text-sm font-bold px-4 py-2 rounded-full bg-[#0D2F4D] text-white hover:bg-[#1a4063] inline-flex items-center gap-1.5"
            >
              <Plus size={14} /> Add Candidate
            </button>
          )}
          {subTab === "employers" && (
            <>
              <button
                data-testid="workspace-import-employers"
                onClick={() => setEmployerImportOpen(true)}
                className="ml-auto text-sm font-bold px-4 py-2 rounded-full bg-white text-[#0D2F4D] border border-[#D6DCE3] hover:border-[#0D2F4D] inline-flex items-center gap-1.5"
              >
                <Upload size={14} /> Import CSV
              </button>
              <button
                data-testid={WORKSPACE.createEmployerBtn}
                onClick={() => setEmployerModal({ open: true, editing: null })}
                className="text-sm font-bold px-4 py-2 rounded-full bg-[#0D2F4D] text-white hover:bg-[#1a4063] inline-flex items-center gap-1.5"
              >
                <Plus size={14} /> Add Employer
              </button>
            </>
          )}
          {subTab === "people" && (
            <button
              data-testid="people-paying-only-toggle"
              onClick={() => setPayingOnly((v) => !v)}
              title={payingOnly ? "Show everyone" : "Only show people who've ever paid (Stripe / PayPal)"}
              className={`ml-auto text-xs font-bold px-3 py-2 rounded-full inline-flex items-center gap-1.5 transition-colors ${
                payingOnly
                  ? "bg-[#C6D21F] text-[#0D2F4D] border border-[#B0BC1A] hover:bg-[#C6D21F]/90"
                  : "bg-white text-[#0D2F4D] border border-[#D6DCE3] hover:border-[#0D2F4D]"
              }`}
            >
              <DollarSign size={12} />
              {payingOnly ? "Paying customers" : "Paying customers only"}
            </button>
          )}
        </div>
      )}

      {subTab === "candidates" && !selectedCandidateId && (
        <div className="mb-6 flex flex-wrap items-center gap-2 px-1" data-testid="candidate-view-presets">
          <Bookmark size={14} className="text-[#6F8FA8]" />
          {candidatePresets.map((preset) => {
            const slug = preset.name.replace(/[^a-z0-9]/gi, "-").toLowerCase();
            return (
              <span key={preset.name} className="inline-flex items-center rounded-full border border-[#D6DCE3] bg-white overflow-hidden">
                <button
                  data-testid={`candidate-preset-load-${slug}`}
                  onClick={() => applyCandidatePreset(preset)}
                  className="px-3 py-1.5 text-xs font-bold text-[#0D2F4D] hover:bg-[#F2F2EF]"
                >
                  {preset.name}
                </button>
                <button
                  data-testid={`candidate-preset-delete-${slug}`}
                  onClick={() => deleteCandidatePreset(preset.name)}
                  title={`Delete ${preset.name}`}
                  className="border-l border-[#E4E7EA] px-2 py-1.5 text-[#6F8FA8] hover:text-red-600"
                >
                  <X size={12} />
                </button>
              </span>
            );
          })}
          <input
            data-testid="candidate-preset-name"
            value={candidatePresetName}
            onChange={(e) => setCandidatePresetName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveCandidatePreset(); }}
            placeholder="Name this view"
            className="min-w-[150px] rounded-full border border-[#D6DCE3] bg-white px-3 py-1.5 text-xs text-[#0D2F4D] outline-none focus:border-[#0D2F4D]"
          />
          <button
            data-testid="candidate-preset-save"
            onClick={saveCandidatePreset}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0D2F4D] px-3 py-1.5 text-xs font-bold text-[#C6D21F] hover:bg-[#1a4063]"
          >
            <Bookmark size={12} /> Save view
          </button>
        </div>
      )}

      {/* Area filter chips — high-visibility Hunter filter for the employer list */}
      {subTab === "employers" && !selectedEmployerId && areas.length > 0 && (
        <div className="flex gap-2 flex-wrap items-center px-1 py-2" data-testid="employer-area-filter">
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8]">Area · Hunter filter</span>
          <button
            onClick={() => setAreaFilter("")}
            data-testid="employer-area-all"
            className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${!areaFilter ? "bg-[#0D2F4D] text-[#C6D21F]" : "bg-white text-[#0D2F4D] border border-[#D6DCE3] hover:border-[#0D2F4D]"}`}
          >
            All · {areas.reduce((s,a) => s + a.count, 0)}
          </button>
          {areas.map(a => (
            <button
              key={a.area}
              onClick={() => setAreaFilter(a.area)}
              data-testid={`employer-area-chip-${a.area.replace(/\s+/g,'-').toLowerCase()}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${areaFilter === a.area ? "bg-[#C6D21F] text-[#0D2F4D]" : "bg-white text-[#0D2F4D] border border-[#D6DCE3] hover:border-[#0D2F4D]"}`}
            >
              {a.area} · {a.count}
            </button>
          ))}
        </div>
      )}

      {/* Body */}
      {selectedCandidateId ? (
        <CandidateDetail
          candidateId={selectedCandidateId}
          pw={pw}
          currentUser={user}
          reloadKey={detailReload}
          onBack={() => { setSelectedCandidateId(null); refresh(); }}
          onRequestEdit={async () => {
            const r = await axios.get(`${BASE}/candidates/${selectedCandidateId}`, { headers: { "X-Admin-Password": pw } });
            setCandidateModal({ open: true, editing: { ...r.data, id: selectedCandidateId } });
          }}
        />
      ) : selectedEmployerId ? (
        <EmployerDetail
          employerId={selectedEmployerId}
          pw={pw}
          currentUser={user}
          reloadKey={detailReload}
          onBack={() => { setSelectedEmployerId(null); refresh(); }}
          onRequestEdit={async () => {
            const r = await axios.get(`${BASE}/employers/${selectedEmployerId}`, { headers: { "X-Admin-Password": pw } });
            setEmployerModal({ open: true, editing: { ...r.data, id: selectedEmployerId } });
          }}
        />
      ) : subTab === "overview" ? (
        <WorkspaceOverview
          pw={pw}
          stats={stats}
          onOpenCandidate={setSelectedCandidateId}
          onOpenEmployer={setSelectedEmployerId}
        />
      ) : subTab === "my-day" ? (
        <MyDayPanel pw={pw} user={user} onOpenEmployer={setSelectedEmployerId} />
      ) : loading ? (
        <p className="text-center text-[#6F8FA8] py-20">Loading…</p>
      ) : subTab === "candidates" ? (
        <CandidatesTable
          rows={candidates}
          onOpen={(row) => setSelectedCandidateId(row.id)}
          pw={pw}
          onBulkComplete={refresh}
          activeFilter={candidateActiveFilter}
        />
      ) : subTab === "abandoned" ? (
        <AbandonedSignupsTable
          rows={abandoned}
          pw={pw}
          onRefresh={loadAbandoned}
          onOpen={(row) => setSelectedCandidateId(row.id)}
        />
      ) : subTab === "employers" ? (
        <EmployersTable
          rows={employers}
          onOpen={(row) => setSelectedEmployerId(row.id)}
          pw={pw}
          onBulkComplete={refresh}
          mode={
            employerStatusFilter === "active_client" ? "active"
              : employerStatusFilter === "prospect" ? "outreach"
              : "all"
          }
        />
      ) : (
        <PeopleTable rows={people} pw={pw} />
      )}

      <CandidateModal
        open={candidateModal.open}
        editing={candidateModal.editing}
        onClose={() => setCandidateModal({ open: false, editing: null })}
        onSaved={() => { refresh(); setDetailReload((k) => k + 1); }}
        onCreated={(id) => setSelectedCandidateId(id)}
        pw={pw}
      />
      <EmployerModal
        open={employerModal.open}
        editing={employerModal.editing}
        team={team}
        defaultStatus={employerStatusFilter || ""}
        onClose={() => setEmployerModal({ open: false, editing: null })}
        onSaved={() => { refresh(); setDetailReload((k) => k + 1); }}
        onCreated={(id) => setSelectedEmployerId(id)}
        pw={pw}
      />
      <EmployerImportDialog
        open={employerImportOpen}
        onClose={() => setEmployerImportOpen(false)}
        onImported={refresh}
        pw={pw}
      />
    </div>
  );
}

/* ─────────────────────────── tables ─────────────────────────── */
function BackfillNotesExtract({ pw, onDone }) {
  // Auto-fill every candidate's profile from Talent Hub questionnaire
  // blobs AND Claude notes-AI extraction. Chunked so a slow Claude
  // run never trips the ingress / axios timeout — we page through in
  // small batches and update a live-progress toast until done.
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");

  const paginate = async (path, pageLimit) => {
    let offset = 0;
    let filled = 0;
    let processed = 0;
    let total = 0;
    // Cap iterations defensively at 200 pages (== up to 4k candidates
    // at pageLimit=20) so a bug can never spin forever.
    for (let i = 0; i < 200; i += 1) {
      const url = `${BASE}${path}?limit=${pageLimit}&offset=${offset}`;
      const r = await axios.post(url, {}, {
        headers: { "X-Admin-Password": pw },
        // 3 min per chunk — pageLimit is small enough to always fit.
        timeout: 180000,
      });
      const d = r?.data || {};
      filled += d.filled || 0;
      processed += d.processed || 0;
      total = d.total || total;
      offset = d.next_offset || (offset + (d.processed || 0));
      if (total) {
        setProgress(`${processed}/${total} scanned · ${filled} filled`);
      }
      if (d.done || (d.processed || 0) === 0) break;
    }
    return { filled, processed, total };
  };

  const run = async () => {
    if (busy) return;
    if (!window.confirm("Auto-fill every candidate's profile from Talent Hub questionnaire AND past call notes? Runs in small batches so it never times out. Safe to re-run — never overwrites existing values.")) return;
    setBusy(true);
    setProgress("Starting…");
    try {
      // Fast pass first — no LLM, 200 per batch.
      setProgress("Flattening Talent Hub questionnaires…");
      const b = await paginate("/candidates/backfill-talent-blobs", 200);
      // Slow pass — Claude, 20 per batch keeps us well under any timeout.
      setProgress("Reading notes with AI…");
      const n = await paginate("/candidates/backfill-notes-extract", 20);
      toast.success(
        `Auto-fill done — Talent Hub filled ${b.filled}, AI notes filled ${n.filled}.`,
      );
      onDone?.();
    } catch (e) {
      const detail = e?.response?.data?.detail
                  || e?.message
                  || "Backfill failed.";
      toast.error(`Backfill failed: ${detail}. Retry — it picks up where it left off.`);
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  return (
    <button
      onClick={run}
      disabled={busy}
      data-testid="backfill-notes-extract-btn"
      className="text-xs font-bold px-3 py-2 rounded-full border border-[#D6DCE3] bg-white text-[#0D2F4D] hover:border-[#0D2F4D] disabled:opacity-50 inline-flex items-center gap-1.5"
      title="Auto-fill every candidate's profile from Talent Hub questionnaire + past notes"
    >
      {busy ? (progress || "Auto-filling…") : "Auto-fill Profiles"}
    </button>
  );
}
function AuditStageListsBtn({ pw, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    if (!window.confirm("Audit every candidate + relocator and sync them onto the Marketing List with the right stage tags? This never sends email — it only fixes list membership and segment tags.")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/audit-stage-lists`, {}, { headers: { "X-Admin-Password": pw } });
      const s = r.data.summary || {};
      toast.success(
        `Audit done · ${s.candidates_scanned} candidates + ${s.relocators_scanned} relocators synced · ` +
        `${(s.candidates_added_to_list || 0) + (s.relocators_added_to_list || 0)} newly added to the list · ` +
        `${(s.candidates_tagged || 0) + (s.relocators_tagged || 0)} stage-tagged`,
        { duration: 8000 }
      );
      onDone?.();
    } catch (e) {
      toast.error(`Audit failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={run}
      disabled={busy}
      data-testid="audit-stage-lists-btn"
      className="text-xs font-bold px-3 py-2 rounded-full border border-[#D6DCE3] bg-white text-[#0D2F4D] hover:border-[#0D2F4D] disabled:opacity-50 inline-flex items-center gap-1.5"
      title="Sync every candidate + relocator onto the Marketing List and tag them by pipeline stage"
    >
      {busy ? "Auditing…" : "Audit → lists"}
    </button>
  );
}



function ActivateEmailedBtn({ pw, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    if (!window.confirm("Mark every candidate who has email communication (sent or received) as Active? New emails auto-activate going forward.")) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/gmail/activate-emailed-candidates`,
        {}, { headers: { "X-Admin-Password": pw } },
      );
      toast.success(`${r.data.activated} candidate${r.data.activated === 1 ? "" : "s"} set to Active.`);
      onDone?.();
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={run}
      disabled={busy}
      data-testid="activate-emailed-btn"
      className="text-xs font-bold px-3 py-2 rounded-full border border-[#D6DCE3] bg-white text-[#0D2F4D] hover:border-[#0D2F4D] disabled:opacity-50 inline-flex items-center gap-1.5"
      title="Mark all candidates with email communication as Active"
    >
      {busy ? "Activating…" : "Activate Emailed"}
    </button>
  );
}


function CandidateBulkImport({ pw, onDone }) {
  // One-click importer for Cara's legacy DB — CSV + optional folder of
  // CVs are uploaded together, CVs auto-match by filename to email or
  // firstname_lastname. Every row defaults to `candidate_type=domestic_au`.
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState(null);
  const [cvFiles, setCvFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!csv) { toast.error("Pick a CSV first"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csv);
      fd.append("default_candidate_type", "domestic_au");
      for (const f of cvFiles) fd.append("cvs", f);
      const r = await axios.post(`${BASE}/candidates/import`, fd, {
        headers: { "X-Admin-Password": pw, "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`${r.data.inserted} added · ${r.data.updated} updated · ${r.data.cvs_attached} CVs matched`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="candidates-bulk-import-open"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-full bg-[#0D2F4D] text-[#C6D21F] text-sm font-bold px-4 py-2 hover:bg-[#0D2F4D]/90"
      >
        <Upload size={14} /> Bulk import
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="candidates-bulk-import-modal"
          onClick={() => { if (!busy) setOpen(false); }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-xl font-extrabold text-[#0D2F4D]">
              Import legacy candidates
            </h3>
            <p className="text-sm text-[#6F8FA8] mt-1">
              Every row is created as <b>Domestic AU</b>. CVs auto-match by
              filename → email local-part or <code>firstname_lastname</code>.
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">
                  Candidates CSV <span className="text-[#B02A2A]">*</span>
                </label>
                <input
                  data-testid="candidates-bulk-import-csv"
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsv(e.target.files?.[0] || null)}
                  className="mt-1 block w-full text-sm text-[#0D2F4D] file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-[#F1F3F5] file:text-[#0D2F4D] file:text-xs file:font-bold"
                />
                <p className="text-[11px] text-[#94A3B8] mt-1">
                  Required column: <code>email</code>. Nice-to-have:
                  first name, last name, phone, industry, role, location,
                  skills, years, linkedin, notes, cv_filename.
                </p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">
                  CVs <span className="text-[#94A3B8] font-normal normal-case">(optional — drop a folder)</span>
                </label>
                <input
                  data-testid="candidates-bulk-import-cvs"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setCvFiles([...(e.target.files || [])])}
                  className="mt-1 block w-full text-sm text-[#0D2F4D] file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-[#F1F3F5] file:text-[#0D2F4D] file:text-xs file:font-bold"
                />
                {cvFiles.length > 0 && (
                  <p className="text-[11px] text-[#0D2F4D] mt-1">{cvFiles.length} CV file(s) queued</p>
                )}
              </div>

              {result && (
                <div
                  className="bg-[#F1F3F5] rounded-xl p-3 text-xs text-[#0D2F4D] space-y-0.5"
                  data-testid="candidates-bulk-import-result"
                >
                  <p><b>{result.inserted}</b> added · <b>{result.updated}</b> updated · <b>{result.skipped}</b> skipped</p>
                  <p><b>{result.cvs_uploaded}</b> CVs uploaded to Object Storage · <b>{result.cvs_attached}</b> auto-matched to a candidate</p>
                  {result.no_account > 0 && (
                    <p className="text-amber-700 font-semibold mt-1" data-testid="import-no-account-note">
                      ⚠ {result.no_account} of these haven't created a SEEK account yet. Close this, filter Candidates by <b>“No account yet”</b>, select them and hit <b>Send SEEK registration</b>.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                disabled={busy}
                className="text-sm text-[#6F8FA8] hover:text-[#0D2F4D] font-semibold px-4 py-2 rounded-full"
              >
                Close
              </button>
              <button
                data-testid="candidates-bulk-import-submit"
                onClick={submit}
                disabled={busy || !csv}
                className="text-sm bg-[#0D2F4D] text-[#C6D21F] font-bold px-5 py-2 rounded-full disabled:opacity-60"
              >
                {busy ? "Importing…" : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function CandidatesTable({ rows, onOpen, pw, onBulkComplete, activeFilter = "active" }) {
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [changingActive, setChangingActive] = useState(false);
  const [exportingBriefs, setExportingBriefs] = useState(false);
  const [rowDeletingId, setRowDeletingId] = useState(null);
  const [sodingToggleId, setSodingToggleId] = useState(null);
  const [recruiterName, setRecruiterName] = useState("Laura Young");
  // "Enrol in automation" — bulk + per-row. `enrollTargets` is the list of
  // {email, first_name} to enrol; the picker opens when it's non-empty.
  const [automations, setAutomations] = useState([]);
  const [enrollTargets, setEnrollTargets] = useState(null);
  const [chosenAuto, setChosenAuto] = useState("");
  const [enrolling, setEnrolling] = useState(false);

  // "Email selected" — manual one-off email to the selected candidates.
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSender, setEmailSender] = useState("");
  const [emailTemplateKey, setEmailTemplateKey] = useState("");
  const [emailTemplatePreview, setEmailTemplatePreview] = useState("");
  const [emailTemplateHasBlocks, setEmailTemplateHasBlocks] = useState(false);
  const emailTargets = rows.filter((r) => selected.has(r.id));
  const emailWithAddr = emailTargets.filter((r) => r.person?.email);

  useEffect(() => {
    const visible = new Set(rows.map((row) => row.id));
    setSelected((prev) => new Set([...prev].filter((id) => visible.has(id))));
  }, [rows]);
  const sendBulkEmail = async () => {
    if (!emailSubject.trim() || (!emailBody.trim() && !emailTemplateKey)) { toast.error("Add a subject and a message."); return; }
    if (emailWithAddr.length === 0) { toast.error("None of the selected candidates have an email address."); return; }
    setEmailSending(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/candidates/bulk-email`,
        { candidate_ids: emailWithAddr.map((c) => c.id), subject: emailSubject, body: emailBody, brand: "careers", sender_admin_email: emailSender || null, template_key: emailTemplateKey || null },
        { headers: { "X-Admin-Password": pw } },
      );
      const { sent = 0, failed = 0, skipped_no_email = 0 } = r.data || {};
      const parts = [`Sent ${sent}`];
      if (failed) parts.push(`${failed} failed`);
      if (skipped_no_email) parts.push(`${skipped_no_email} had no email`);
      if (sent > 0) toast.success(parts.join(" · ") + "."); else toast.error(parts.join(" · ") + ".");
      setEmailOpen(false); setEmailSubject(""); setEmailBody(""); setEmailTemplateKey(""); setEmailTemplatePreview(""); setEmailTemplateHasBlocks(false);
      clearSel();
    } catch (e) {
      toast.error(`Email failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setEmailSending(false); }
  };

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/automations`, { headers: { "X-Admin-Password": pw } })
      .then(r => setAutomations((r.data.automations || []).filter(a => a.active)))
      .catch(() => {});
  }, [pw]);

  const openEnroll = (targets) => {
    const clean = (targets || []).filter(t => t.email);
    if (clean.length === 0) { toast.error("No email on the selected candidate(s) to enrol."); return; }
    setEnrollTargets(clean);
    setChosenAuto("");
  };
  const runEnroll = async () => {
    if (!chosenAuto) { toast.error("Pick an automation."); return; }
    setEnrolling(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/automations/enroll-bulk`,
        { automation_id: chosenAuto, contacts: enrollTargets },
        { headers: { "X-Admin-Password": pw } },
      );
      const { enrolled = 0, already_in = 0, skipped = 0 } = r.data || {};
      const parts = [`Enrolled ${enrolled}`];
      if (already_in) parts.push(`${already_in} already in`);
      if (skipped) parts.push(`${skipped} skipped`);
      toast.success(parts.join(" · ") + ".");
      setEnrollTargets(null);
      clearSel();
    } catch (e) {
      toast.error(`Enrol failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setEnrolling(false); }
  };

  const deleteOne = async (c) => {
    const name = `${c?.person?.first_name || ""} ${c?.person?.last_name || ""}`.trim() || c?.person?.email || "this candidate";
    if (!window.confirm(`Delete ${name}?\n\nThis removes the candidate record + their Person contact (if not linked elsewhere). This can't be undone.`)) return;
    setRowDeletingId(c.id);
    try {
      await axios.delete(
        `${BASE}/candidates/${c.id}?purge_person=true`,
        { headers: { "X-Admin-Password": pw } },
      );
      toast.success(`Deleted ${name}.`);
      onBulkComplete?.();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setRowDeletingId(null); }
  };

  const toggleSodingClient = async (candidate) => {
    const isActive = !!candidate.soding_client;
    setSodingToggleId(candidate.id);
    try {
      await axios.patch(
        `${BASE}/candidates/${candidate.id}`,
        { soding_client: !isActive },
        { headers: { "X-Admin-Password": pw } },
      );
      toast.success(isActive ? "Soding Client removed." : "Marked as Soding Client.");
      onBulkComplete?.();
    } catch (error) {
      toast.error(`Soding Client update failed: ${error?.response?.data?.detail || error.message}`);
    } finally {
      setSodingToggleId(null);
    }
  };

  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => setSelected(prev => {
    if (prev.size === rows.length) return new Set();
    return new Set(rows.map(r => r.id));
  });
  const clearSel = () => setSelected(new Set());

  const bulkSend = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Send SEEK registration email to ${selected.size} candidate${selected.size===1?"":"s"}?\n(Already-sent candidates will be skipped.)`)) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/candidates/bulk-send-seek-registration`,
        {
          candidate_ids: Array.from(selected),
          recruiter_name: recruiterName || "The Ditch Careers team",
          skip_already_sent: true,
        },
        { headers: { "X-Admin-Password": pw } },
      );
      const { sent, skipped, failed, total } = r.data;
      toast.success(`Sent ${sent}/${total} · skipped ${skipped} · failed ${failed}`);
      clearSel();
      onBulkComplete?.();
    } catch (e) {
      toast.error(`Bulk send failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSending(false); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const purge = window.confirm(
      `Delete ${selected.size} candidate${selected.size === 1 ? "" : "s"}?\n\n` +
      `OK  = Delete candidate record + their Person contact (if not linked elsewhere)\n` +
      `Cancel this dialog to abort.`,
    );
    if (!purge) return;
    setDeleting(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/candidates/bulk-delete`,
        { ids: Array.from(selected), purge_person: true },
        { headers: { "X-Admin-Password": pw } },
      );
      const { count, requested } = r.data;
      toast.success(`Deleted ${count} of ${requested} candidates.`);
      clearSel();
      onBulkComplete?.();
    } catch (e) {
      toast.error(`Bulk delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setDeleting(false); }
  };

  const bulkSetActive = async () => {
    if (selected.size === 0) return;
    const makeActive = activeFilter === "inactive";
    if (!window.confirm(
      `${makeActive ? "Reactivate" : "Mark"} ${selected.size} candidate${selected.size === 1 ? "" : "s"} ${makeActive ? "as active" : "inactive"}?` +
      (makeActive ? "\n\nTheir previous marketing unsubscribe will stay in place." : "\n\nThis keeps their CRM history, removes them from marketing, and stops active automations."),
    )) return;
    setChangingActive(true);
    try {
      const r = await axios.post(
        `${BASE}/candidates/bulk-active`,
        { ids: Array.from(selected), active: makeActive },
        { headers: { "X-Admin-Password": pw } },
      );
      toast.success(makeActive
        ? `Reactivated ${r.data.updated} candidate${r.data.updated === 1 ? "" : "s"}.`
        : `Marked ${r.data.updated} inactive · ${r.data.emails_suppressed || 0} removed from marketing.`);
      clearSel();
      onBulkComplete?.();
    } catch (e) {
      toast.error(`Status update failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setChangingActive(false); }
  };

  const exportJobSearchBriefs = async () => {
    if (selected.size === 0) return;
    setExportingBriefs(true);
    const toastId = "candidate-job-search-briefs";
    try {
      toast.loading("Building Claude Chrome job-search pack…", { id: toastId });
      const response = await axios.post(
        `${BASE}/candidates/export-job-search-briefs`,
        { candidate_ids: Array.from(selected) },
        { headers: { "X-Admin-Password": pw }, responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `candidate-job-search-briefs-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Exported ${selected.size} candidate brief${selected.size === 1 ? "" : "s"}.`, { id: toastId });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Job-search brief export failed.", { id: toastId });
    } finally { setExportingBriefs(false); }
  };

  if (!rows.length) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center border border-[#E4E7EA]">
        <p className="text-[#6F8FA8]">No candidates yet — click <strong>New candidate</strong> to add the first.</p>
      </div>
    );
  }
  const allSelected = selected.size === rows.length && rows.length > 0;
  const someSelected = selected.size > 0 && !allSelected;
  return (
    <div className="space-y-3">
      {/* Bulk action bar — only shows when at least one row is selected */}
      {selected.size > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 bg-[#0D2F4D] text-white rounded-2xl px-5 py-3 border border-[#0D2F4D]"
          data-testid="candidates-bulk-bar"
        >
          <span className="text-sm font-semibold">
            {selected.size} selected
          </span>
          <button
            onClick={bulkSetActive}
            disabled={changingActive}
            data-testid="candidates-bulk-active-toggle"
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#C6D21F]/90 disabled:opacity-40 transition-colors"
          >
            {activeFilter === "inactive" ? <RotateCcw size={12}/> : <UserX size={12}/>}
            {changingActive ? "Updating…" : activeFilter === "inactive" ? `Reactivate ${selected.size}` : `Mark ${selected.size} inactive`}
          </button>
          <input
            type="text"
            value={recruiterName}
            onChange={(e) => setRecruiterName(e.target.value)}
            placeholder="Recruiter name"
            data-testid="candidates-bulk-recruiter"
            className="text-xs bg-white/10 text-white placeholder-white/60 rounded-lg px-3 py-1.5 outline-none focus:bg-white/20 border border-white/20"
          />
          <button
            onClick={bulkSend}
            disabled={sending || activeFilter === "inactive"}
            data-testid="candidates-bulk-send-seek"
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#C6D21F]/90 disabled:opacity-40 transition-colors"
          >
            {sending ? "Sending…" : `Send SEEK registration to ${selected.size}`}
          </button>
          <button
            onClick={async () => {
              // Bulk Download ZIP — Company CV template stamped for every
              // selected candidate. Streams a real zip; kept as a plain
              // <a>-click after fetch so Chrome's popup-blocker never
              // intercepts (same pattern as the CV button fix).
              const ids = Array.from(selected);
              const toastId = "bulk-cvs";
              try {
                toast.loading(`Rendering ${ids.length} CV${ids.length===1?"":"s"}…`, { id: toastId });
                const r = await fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/candidates/bulk-branded-cv.zip`,
                  {
                    method: "POST",
                    headers: { "X-Admin-Password": pw, "Content-Type": "application/json" },
                    body: JSON.stringify({ candidate_ids: ids }),
                  }
                );
                if (!r.ok) {
                  let msg = `HTTP ${r.status}`;
                  try { const j = await r.json(); if (j?.detail) msg = j.detail; } catch (_) {}
                  throw new Error(msg);
                }
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `ditch-careers-cvs-${new Date().toISOString().slice(0,10)}.zip`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 5000);
                toast.success(`Downloaded ${ids.length} CV${ids.length===1?"":"s"} as ZIP`, { id: toastId });
              } catch (e) {
                toast.error(`Bulk CV failed: ${e.message}`, { id: toastId });
              }
            }}
            data-testid="candidates-bulk-download-cvs"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-full bg-white text-[#0D2F4D] hover:bg-white/90 disabled:opacity-40 transition-colors"
          >
            <FileText size={12}/> Download {selected.size} CV{selected.size===1?"":"s"} (ZIP)
          </button>
          <button
            onClick={exportJobSearchBriefs}
            disabled={exportingBriefs}
            data-testid="candidates-export-job-search-briefs"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-full bg-white text-[#0D2F4D] hover:bg-white/90 disabled:opacity-40 transition-colors"
          >
            <Briefcase size={12}/>{exportingBriefs ? "Building search pack…" : "Export Job Search Briefs"}
          </button>
          <button
            onClick={() => openEnroll(rows.filter(r => selected.has(r.id)).map(r => ({ email: r.person?.email, first_name: r.person?.first_name })))}
            disabled={activeFilter === "inactive"}
            data-testid="candidates-bulk-enroll"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-full bg-white text-[#0D2F4D] hover:bg-white/90 disabled:opacity-40 transition-colors"
          >
            <Repeat size={12}/> Enrol {selected.size} in automation
          </button>
          <button
            onClick={() => { setEmailSubject(""); setEmailBody(""); setEmailOpen(true); }}
            disabled={activeFilter === "inactive"}
            data-testid="candidates-bulk-email"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-full bg-white text-[#0D2F4D] hover:bg-white/90 disabled:opacity-40 transition-colors"
          >
            <Mail size={12}/> Email {selected.size}
          </button>
          <button
            onClick={bulkDelete}
            disabled={deleting}
            data-testid="candidates-bulk-delete"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 transition-colors"
          >
            <Trash2 size={12}/> {deleting ? "Deleting…" : `Delete ${selected.size}`}
          </button>
          <button
            onClick={clearSel}
            data-testid="candidates-bulk-clear"
            className="text-xs text-white/70 hover:text-white"
          >
            Clear
          </button>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-[#E4E7EA] overflow-x-auto">
        <table className="w-full text-sm md:min-w-[900px]">
          <thead className="bg-[#F2F2EF] text-[10px] uppercase tracking-widest text-[#6F8FA8]">
            <tr>
              <th className="px-3 py-3 w-[40px]">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => el && (el.indeterminate = someSelected)}
                  onChange={toggleAll}
                  data-testid="candidates-select-all"
                  className="w-4 h-4 rounded cursor-pointer"
                />
              </th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3 hidden sm:table-cell">Created</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Stage</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Type</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Industry</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Location target</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Situation</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">CV</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Tags</th>
              <th className="text-right px-4 py-3 hidden sm:table-cell"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr
                key={c.id}
                data-testid={WORKSPACE.candidateRow(c.id)}
                className={cx(
                  "border-t border-[#F2F2EF] hover:bg-[#FAFAF7]",
                  selected.has(c.id) && "bg-[#C6D21F]/10",
                )}
              >
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggle(c.id)}
                    data-testid={`candidate-select-${c.id}`}
                    className="w-4 h-4 rounded cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td className="px-4 py-3">
                  <p className="font-semibold text-[#0D2F4D] flex items-center gap-1.5">
                    {c.person?.first_name} {c.person?.last_name}
                    <button
                      type="button"
                      data-testid={`candidate-soding-client-${c.id}`}
                      aria-label={`${c.soding_client ? "Remove" : "Mark as"} Soding Client`}
                      aria-pressed={!!c.soding_client}
                      title={c.soding_client ? "Soding Client — click to remove" : "Mark as Soding Client"}
                      disabled={sodingToggleId === c.id}
                      onClick={(event) => { event.stopPropagation(); toggleSodingClient(c); }}
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-black transition-[background-color,border-color,color,transform] hover:scale-105 disabled:opacity-50 ${
                        c.soding_client
                          ? "border-[#0D2F4D] bg-[#0D2F4D] text-[#C6D21F]"
                          : "border-[#D6DCE3] bg-white text-[#94A3B8] hover:border-[#0D2F4D] hover:text-[#0D2F4D]"
                      }`}
                    >
                      S
                    </button>
                    {c.active === false && (
                      <span
                        data-testid={`candidate-inactive-${c.id}`}
                        className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] bg-[#F2F2EF] border border-[#D6DCE3] rounded-full px-2 py-0.5"
                      >
                        Inactive
                      </span>
                    )}
                    {c.person?._orphan && (
                      <span
                        title="Person record missing — data self-healed. Consider deleting."
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-red-600"
                        data-testid={`candidate-orphan-${c.id}`}
                      >
                        <AlertTriangle size={10}/> orphan
                      </span>
                    )}
                    {c.account_created === false && (
                      <span
                        title="Uploaded — hasn't created a SEEK account yet. Send them the SEEK registration email."
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                        data-testid={`candidate-no-account-${c.id}`}
                      >
                        No account yet
                      </span>
                    )}
                    {c.seek_registration_sent_at && (
                      c.seek_registration_status === "failed" ? (
                        <span
                          title={`SEEK registration email FAILED to send on ${new Date(c.seek_registration_sent_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5"
                          data-testid={`candidate-seek-failed-${c.id}`}
                        >
                          <AlertTriangle size={10}/> SEEK email failed
                        </span>
                      ) : (
                        <span
                          title={`SEEK registration email sent ${new Date(c.seek_registration_sent_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                          data-testid={`candidate-seek-sent-${c.id}`}
                        >
                          <CheckCircle2 size={10}/> SEEK email sent
                        </span>
                      )
                    )}
                  </p>
                  {/* Headline (Feb 2026): show the candidate's professional
                      one-liner right under the name so recruiters skim the
                      table without opening every profile. Falls back to the
                      current-role summary or role_level when a headline was
                      never captured. */}
                  {(c.headline || c.next_role?.title || c.role_level) && (
                    <p
                      className="text-xs text-[#33475F] mt-0.5 line-clamp-1"
                      title={c.headline || c.next_role?.title || c.role_level}
                      data-testid={`candidate-headline-${c.id}`}
                    >
                      {c.headline || c.next_role?.title || c.role_level}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-xs text-[#6F8FA8]">
                    <span data-testid={`candidate-email-${c.id}`}>{c.person?.email || "—"}</span>
                    {c.person?.phone
                      ? (
                        <a
                          href={`tel:${c.person.phone}`}
                          onClick={(evt) => evt.stopPropagation()}
                          data-testid={`candidate-phone-${c.id}`}
                          className="inline-flex items-center gap-1 font-semibold text-[#0D2F4D] hover:text-[#C6D21F] no-underline"
                        >
                          <Phone size={11} /> {c.person.phone}
                        </a>
                      )
                      : (
                        <span
                          data-testid={`candidate-phone-missing-${c.id}`}
                          className="inline-flex items-center gap-1 font-semibold text-red-500"
                          title="No phone number on file"
                        >
                          <Phone size={11} /> No phone
                        </span>
                      )}
                  </div>
                  <a
                    href={`/admin?tab=candidates&candidate=${c.id}`}
                    data-testid={`candidate-open-mobile-${c.id}`}
                    onClick={(evt) => {
                      if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey || evt.button === 1) return;
                      evt.preventDefault();
                      onOpen(c);
                    }}
                    className="sm:hidden mt-2 text-xs font-bold text-[#0D2F4D] inline-flex items-center gap-1 no-underline"
                  >
                    Open profile <ArrowRight size={12} />
                  </a>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell whitespace-nowrap text-xs text-[#33475F]" data-testid={`candidate-created-${c.id}`}>
                  {c.created_at
                    ? new Date(c.created_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })
                    : "—"}
                </td>
                <td className="px-4 py-3 hidden md:table-cell" data-testid={`candidate-stage-${c.id}`}>
                  <StagePill stage={c.stage || "new_candidate"} />
                </td>
                <td className="px-4 py-3 hidden md:table-cell" data-testid={`candidate-type-${c.id}`}>
                  {c.candidate_type === "relocating" ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#0D2F4D]/10 text-[#0D2F4D] whitespace-nowrap">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0D2F4D]"/> Relocating
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#F0F5EA] text-[#5A6D0F] whitespace-nowrap">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C6D21F]"/> Domestic AU
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-[#0D2F4D] hidden md:table-cell" data-testid={`candidate-industry-${c.id}`}>
                  {c.industry
                    || c.sector
                    || c.category
                    || c.next_role?.industry
                    || c.employment?.industry
                    || "—"}
                </td>
                <td className="px-4 py-3 text-[#0D2F4D] hidden lg:table-cell" data-testid={`candidate-location-target-${c.id}`}>
                  {c.location_target
                    || c.target_location
                    || c.desired_location
                    || c.next_role?.location
                    || c.next_role?.target_location
                    || "—"}
                </td>
                <td className="px-4 py-3 text-[#0D2F4D] hidden lg:table-cell max-w-[260px]" data-testid={`candidate-situation-${c.id}`}>
                  {c.situation ? (
                    <span title={c.situation} className="line-clamp-2 text-xs leading-snug">
                      {c.situation}
                    </span>
                  ) : (
                    <span className="text-[#94A3B8]">—</span>
                  )}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell" data-testid={`candidate-cv-${c.id}`}>
                  {(() => {
                    const cv = pickCvDoc(c);
                    if (!cv) {
                      // Missing-CV state (Feb 2026): loud red badge so
                      // recruiters spot it instantly, plus a one-tap
                      // "Ask to re-upload" that fires the branded email.
                      const already = !!c.cv_reupload_sent_at;
                      return (
                        <div className="flex flex-col items-start gap-1" data-testid={`candidate-cv-missing-${c.id}`}>
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-red-50 text-red-700 border border-red-200"
                            title="No CV file linked to this candidate"
                          >
                            <AlertTriangle size={10}/> Missing CV
                          </span>
                          {already ? (
                            <span
                              className="text-[10px] font-bold uppercase tracking-widest text-[#6F8FA8]"
                              title={`Re-upload email sent ${c.cv_reupload_sent_at}`}
                            >
                              Re-upload email sent
                            </span>
                          ) : (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm(
                                  `Send the re-upload request email to ${c.person?.first_name || "this candidate"}?`,
                                )) return;
                                try {
                                  const r = await axios.post(
                                    `${BASE}/candidates/${c.id}/request-cv-reupload`,
                                    {},
                                    { headers: { "X-Admin-Password": pw } },
                                  );
                                  toast.success(`Sent to ${r.data.sent_to}.`);
                                  onBulkComplete?.();
                                } catch (err) {
                                  const msg = err?.response?.data?.detail || err.message;
                                  toast.error(`Send failed: ${msg}`);
                                }
                              }}
                              data-testid={`candidate-cv-reupload-${c.id}`}
                              className="text-[10px] font-bold text-red-700 underline hover:text-red-800"
                            >
                              Ask to re-upload
                            </button>
                          )}
                        </div>
                      );
                    }
                    // Route CV downloads through our backend proxy so the
                    // Careers app's X-API-Key auth is handled server-to-server.
                    // <a href> can't send headers, so admin token rides as ?pw.
                    const proxyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/candidates/${c.id}/cv-proxy?pw=${encodeURIComponent(pw)}`;
                    return (
                      <a
                        href={proxyUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`candidate-cv-link-${c.id}`}
                        className="text-xs font-bold text-[#0D2F4D] underline hover:text-[#C6D21F] inline-flex items-center gap-1"
                        title={cv.name || cv.filename || "Open CV (proxied)"}
                      >
                        <FileText size={12} /> CV
                      </a>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell"><TagPills tags={c.person?.tags || []} /></td>
                <td className="px-4 py-3 text-right hidden sm:table-cell">
                  <button
                    data-testid={`candidate-delete-${c.id}`}
                    onClick={(e) => { e.stopPropagation(); deleteOne(c); }}
                    disabled={rowDeletingId === c.id}
                    title="Delete candidate"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full p-1.5 mr-1 disabled:opacity-40 inline-flex items-center align-middle"
                  >
                    <Trash2 size={14} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); openEnroll([{ email: c.person?.email, first_name: c.person?.first_name }]); }}
                    data-testid={`candidate-enroll-${c.id}`}
                    className="text-xs font-bold text-[#0D2F4D] hover:text-[#C6D21F] inline-flex items-center gap-1"
                    title="Enrol this candidate into an automation"
                  >
                    <Repeat size={12} /> Enrol
                  </button>
                  <a
                    href={`/admin?tab=candidates&candidate=${c.id}`}
                    data-testid={WORKSPACE.candidateOpen(c.id)}
                    onClick={(evt) => {
                      if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey || evt.button === 1) return;
                      evt.preventDefault();
                      onOpen(c);
                    }}
                    className="text-xs font-bold text-[#0D2F4D] hover:text-[#C6D21F] inline-flex items-center gap-1 no-underline"
                  >
                    Open <ArrowRight size={12} />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!enrollTargets} onOpenChange={(o) => !o && setEnrollTargets(null)}>
        <DialogContent className="max-w-md" data-testid="enroll-automation-modal">
          <DialogHeader>
            <DialogTitle>Enrol in automation</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6F8FA8]">
            Enrolling <strong>{enrollTargets?.length || 0}</strong> candidate{(enrollTargets?.length || 0) === 1 ? "" : "s"}. Pick an active automation to start their journey.
          </p>
          <SdSelect value={chosenAuto} onValueChange={setChosenAuto}>
            <SelectTrigger data-testid="enroll-automation-select"><SelectValue placeholder="Choose an automation…"/></SelectTrigger>
            <SelectContent>
              {automations.length === 0
                ? <SelectItem value="__none__" disabled>No active automations — turn one on first</SelectItem>
                : automations.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </SdSelect>
          <DialogFooter>
            <button onClick={() => setEnrollTargets(null)} className="text-sm font-medium px-4 py-2 rounded-full text-[#6F8FA8] hover:text-[#0D2F4D]">Cancel</button>
            <button
              onClick={runEnroll}
              disabled={enrolling || !chosenAuto || chosenAuto === "__none__"}
              data-testid="enroll-automation-confirm"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90 disabled:opacity-50"
            >
              <Repeat size={13}/>{enrolling ? "Enrolling…" : "Enrol"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual bulk email composer */}
      <Dialog open={emailOpen} onOpenChange={(o) => !o && setEmailOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="bulk-email-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={16}/> Email selected candidates</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6F8FA8]">
            One-off email to <strong>{emailWithAddr.length}</strong> candidate{emailWithAddr.length === 1 ? "" : "s"}
            {emailTargets.length !== emailWithAddr.length && (
              <> · {emailTargets.length - emailWithAddr.length} skipped (no email)</>
            )}. Use <code className="text-[11px] bg-[#F2F2EF] px-1 rounded">{"{{first_name}}"}</code> to personalise.
          </p>
          <EmailComposerExtras
            pw={pw}
            brand="careers"
            senderEmail={emailSender}
            onSender={setEmailSender}
            selectedKey={emailTemplateKey}
            onLoadTemplate={(t) => { setEmailTemplateKey(t?.key || ""); setEmailSubject(t?.subject || ""); setEmailBody(t?.body || ""); setEmailTemplatePreview(t?.preview_html || ""); setEmailTemplateHasBlocks(Boolean(t?.blocks?.length)); }}
          />
          <input
            type="text"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Subject"
            data-testid="bulk-email-subject"
            className="w-full text-sm rounded-lg border border-[#D6DCE3] px-3 py-2 outline-none focus:border-[#0D2F4D]"
          />
          {emailTemplateHasBlocks && emailTemplatePreview ? (
            <div data-testid="bulk-email-template-preview" className="space-y-2">
              <p className="text-xs font-semibold text-[#0D2F4D]">Full Email Hub design loaded. Links and layout will send exactly as previewed.</p>
              <iframe title="Selected email template preview" srcDoc={emailTemplatePreview} className="h-[360px] w-full rounded-lg border border-[#D6DCE3] bg-white" sandbox=""/>
            </div>
          ) : <textarea
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            placeholder={"Hi {{first_name}},\n\nWrite your message here…"}
            rows={8}
            data-testid="bulk-email-body"
            className="w-full text-sm rounded-lg border border-[#D6DCE3] px-3 py-2 outline-none focus:border-[#0D2F4D] resize-y"
          />}
          <DialogFooter>
            <button onClick={() => setEmailOpen(false)} className="text-sm font-medium px-4 py-2 rounded-full text-[#6F8FA8] hover:text-[#0D2F4D]">Cancel</button>
            <button
              onClick={sendBulkEmail}
              disabled={emailSending || emailWithAddr.length === 0}
              data-testid="bulk-email-send"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90 disabled:opacity-50"
            >
              <Mail size={13}/>{emailSending ? "Sending…" : `Send to ${emailWithAddr.length}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
 * MyDayPanel — recruiter's personal command centre.
 * ═══════════════════════════════════════════════════════════════════════ */
/**
 * MyDayTaskCreator — inline task punch-in for the My Day tab.
 * Same schema as TasksSection but lives standalone (no entity link
 * required, so it acts as a personal todo lane). Optional entity_type
 * would need cross-app search — kept scoped to standalone tasks here
 * so recruiters can capture "call bookkeeper" without cluttering a
 * candidate profile.
 */
const TEAM_OPTIONS = [
  { label: "Cara",  email: "cara@theditchcollective.co.nz" },
  { label: "Priya", email: "priya@theditchcollective.co.nz" },
  { label: "Laura", email: "laura@theditchcollective.co.nz" },
  { label: "Holly", email: "holly@theditchcollective.co.nz" },
];

/** Inline quick-add row for a column. Type + Enter → task created with
 * due_date set to match the column. Assignee defaults to the current
 * user OR the active board filter. Escape closes without saving. */
function ColumnQuickAdd({ pw, me, columnKey, defaultAssignee, onCreated, disabled }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const dueFor = (col) => {
    const d = new Date();
    if (col === "overdue") d.setDate(d.getDate() - 1);
    else if (col === "due_this_week") d.setDate(d.getDate() + 3);
    else if (col === "this_month") {
      const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      const offset = Math.max(8, Math.floor((monthEnd.getDate() - d.getDate()) / 2));
      d.setDate(d.getDate() + offset);
    }
    return d.toISOString().slice(0, 10);
  };
  const submit = async () => {
    const t = title.trim();
    if (!t) { setOpen(false); return; }
    setBusy(true);
    try {
      await axios.post(`${BASE}/tasks`, {
        title: t,
        due_date: dueFor(columnKey),
        assigned_to: defaultAssignee || me,
        recurrence: "none",
      }, { headers: { "X-Admin-Password": pw, "X-Admin-User": me } });
      setTitle("");
      setOpen(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add task.");
    } finally { setBusy(false); }
  };
  if (disabled) {
    return (
      <p className="w-full mt-2 text-left text-[10px] text-[#94A3B8] italic py-2 px-2">
        Recurring tasks appear here based on their repeat setting.
      </p>
    );
  }
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid={`column-quickadd-${columnKey}`}
        className="w-full mt-2 text-left text-[11px] text-[#6F8FA8] hover:text-[#0D2F4D] py-2 px-2 rounded-lg hover:bg-white/50 transition-colors flex items-center gap-1.5"
      >
        <Plus size={11}/> Add a task
      </button>
    );
  }
  return (
    <div className="mt-2 p-2 rounded-lg bg-white border border-[#D6DCE3] shadow-sm">
      <input
        type="text"
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
          if (e.key === "Escape") { setTitle(""); setOpen(false); }
        }}
        onBlur={() => { if (!title.trim()) setOpen(false); }}
        placeholder="Task title — Enter to save, Esc to cancel"
        data-testid={`column-quickadd-input-${columnKey}`}
        disabled={busy}
        className="w-full bg-transparent text-sm text-[#0D2F4D] focus:outline-none placeholder:text-[#94A3B8]"
      />
    </div>
  );
}


function MyDayTaskCreator({ pw, me, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", notes: "", due_date: "", assigned_to: me, recurrence: "none",
  });
  useEffect(() => { setForm((f) => ({ ...f, assigned_to: me })); }, [me]);
  const submit = async () => {
    if (!form.title.trim()) { toast.error("Task title is required."); return; }
    setBusy(true);
    try {
      await axios.post(`${BASE}/tasks`, form, {
        headers: { "X-Admin-Password": pw, "X-Admin-User": me },
      });
      toast.success("Task added.");
      setForm({ title: "", notes: "", due_date: "", assigned_to: me, recurrence: "none" });
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add task.");
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-[#E4E7EA] p-4" data-testid="my-day-new-task">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          data-testid="my-day-new-task-btn"
          className="w-full text-left text-sm text-[#6F8FA8] hover:text-[#0D2F4D] flex items-center gap-2 transition-colors"
        >
          <Plus size={14} className="text-[#0D2F4D]" />
          Add a task to your day…
        </button>
      ) : (
        <div className="space-y-2" data-testid="my-day-new-task-form">
          <input
            type="text"
            autoFocus
            placeholder="Task title (e.g. Write monthly newsletter)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
            data-testid="my-day-new-task-title"
            className="w-full bg-white border border-[#D6DCE3] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0D2F4D]"
          />
          <textarea
            rows={2}
            placeholder="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="my-day-new-task-notes"
            className="w-full bg-white border border-[#D6DCE3] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0D2F4D] resize-y"
          />
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#6F8FA8] flex items-center gap-1"><Calendar size={9}/> Due</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="my-day-new-task-due"
                className="w-full mt-1 bg-white border border-[#D6DCE3] rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#0D2F4D]"
              />
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#6F8FA8] flex items-center gap-1"><Users size={9}/> Assign</label>
              <select
                value={form.assigned_to}
                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                data-testid="my-day-new-task-assignee"
                className="w-full mt-1 bg-white border border-[#D6DCE3] rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#0D2F4D]"
              >
                <option value="">Unassigned</option>
                {TEAM_OPTIONS.map((t) => <option key={t.email} value={t.email}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#6F8FA8] flex items-center gap-1"><Repeat size={9}/> Repeat</label>
              <select
                value={form.recurrence}
                onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
                data-testid="my-day-new-task-recurrence"
                className="w-full mt-1 bg-white border border-[#D6DCE3] rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#0D2F4D]"
              >
                <option value="none">No repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 weeks</option>
                <option value="monthly">Monthly</option>
                <option value="mid_monthly">15th of each month</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={submit}
              disabled={busy}
              data-testid="my-day-new-task-save"
              className="text-[11px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#d3df34] disabled:opacity-40 transition-all"
            >{busy ? "Saving…" : "Add task"}</button>
            <button
              onClick={() => setOpen(false)}
              className="text-[11px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full text-[#6F8FA8] hover:text-[#0D2F4D]"
            >Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}


/** Right-side detail drawer for a My Day task card. Full comment thread,
 * quick-status buttons (Today / Tomorrow / +1 week / Done), and inline
 * reply. Overlay closes on backdrop click or Esc. */
function TaskDetailDrawer({ task, pw, me, onClose, onChanged }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const H = { headers: { "X-Admin-Password": pw, "X-Admin-User": me } };
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  const iso = (d) => d.toISOString().slice(0, 10);
  const setDue = async (target) => {
    const d = new Date();
    if (target === "tomorrow") d.setDate(d.getDate() + 1);
    else if (target === "week") d.setDate(d.getDate() + 7);
    try {
      await axios.patch(`${BASE}/tasks/${task.id}`, { due_date: iso(d) }, H);
      onChanged?.();
    } catch { toast.error("Couldn't update task."); }
  };
  const markDone = async () => {
    try { await axios.patch(`${BASE}/tasks/${task.id}`, { status: "done" }, H); onChanged?.(); onClose(); }
    catch { toast.error("Couldn't mark done."); }
  };
  const sendReply = async () => {
    const body = reply.trim();
    if (!body) return;
    setBusy(true);
    try {
      await axios.post(`${BASE}/tasks/${task.id}/comments`, { body }, H);
      setReply("");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post reply.");
    } finally { setBusy(false); }
  };
  const fmt = (iso) => {
    if (!iso) return "";
    try { const d = new Date(iso); const diffH = (Date.now() - d.getTime())/36e5;
      if (diffH < 1) return `${Math.max(1, Math.floor(diffH*60))}m ago`;
      if (diffH < 24) return `${Math.floor(diffH)}h ago`;
      return d.toLocaleDateString();
    } catch { return ""; }
  };
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="task-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md h-full bg-white shadow-2xl overflow-y-auto" data-testid="task-drawer-panel">
        <div className="p-5 border-b border-[#E4E7EA] flex items-start gap-3">
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-[#0D2F4D]">Task</p>
            <h3 className="font-display text-2xl font-bold text-[#0D2F4D] leading-tight mt-1">{task.title}</h3>
            <p className="text-xs text-[#6F8FA8] mt-2">
              {task.due_date && <>Due {task.due_date} · </>}
              Assigned to <span className="font-semibold text-[#0D2F4D]">{(task.assigned_to || "unassigned").split("@")[0]}</span>
              {task.recurrence && task.recurrence !== "none" && <> · repeats {task.recurrence}</>}
            </p>
            {task.notes && <p className="text-sm text-[#0D2F4D] mt-3 whitespace-pre-wrap leading-relaxed">{task.notes}</p>}
          </div>
          <button onClick={onClose} data-testid="task-drawer-close" className="text-[#6F8FA8] hover:text-[#0D2F4D]"><X size={18}/></button>
        </div>
        <div className="p-5 flex flex-wrap gap-2 border-b border-[#E4E7EA]">
          <button onClick={() => setDue("tomorrow")} data-testid="task-drawer-tomorrow" className="text-[10px] font-bold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full bg-white border border-[#D6DCE3] text-[#0D2F4D] hover:border-[#0D2F4D]">Tomorrow</button>
          <button onClick={() => setDue("week")}     data-testid="task-drawer-week"     className="text-[10px] font-bold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full bg-white border border-[#D6DCE3] text-[#0D2F4D] hover:border-[#0D2F4D]">+1 week</button>
          <button onClick={markDone}                  data-testid="task-drawer-done"     className="text-[10px] font-bold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#d3df34]">Mark done</button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[10px] uppercase tracking-[0.22em] font-black text-[#0D2F4D]">Comments</p>
          {(task.comments || []).length === 0 && (
            <p className="text-xs text-[#94A3B8] italic">No comments yet.</p>
          )}
          {(task.comments || []).map((c) => (
            <div key={c.id} className="p-3 rounded-lg bg-[#FAFAF7] border border-[#F2F2EF]" data-testid={`task-drawer-comment-${c.id}`}>
              <div className="flex items-baseline justify-between text-[10px] text-[#6F8FA8]">
                <span className="font-bold text-[#0D2F4D]">{(c.author || "").split("@")[0]}</span>
                <span>{fmt(c.created_at)}</span>
              </div>
              <p className="text-sm text-[#0D2F4D] mt-1 whitespace-pre-wrap leading-snug">{c.body}</p>
            </div>
          ))}
          <textarea
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendReply(); }}
            placeholder="Add a reply — @cara / @priya / @laura / @holly to tag someone — ⌘/Ctrl+Enter to send"
            data-testid="task-drawer-reply"
            className="w-full bg-white border border-[#D6DCE3] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0D2F4D] resize-y"
          />
          <button
            onClick={sendReply}
            disabled={busy}
            data-testid="task-drawer-reply-send"
            className="text-[11px] font-bold uppercase tracking-[0.14em] px-4 py-2 rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#1a4063] disabled:opacity-40"
          >{busy ? "Sending…" : "Send reply"}</button>
        </div>
      </div>
    </div>
  );
}



function MyDayPanel({ pw, user, onOpenEmployer }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("ditch_dismissed_assignments") || "[]")); }
    catch { return new Set(); }
  });
  // Manual-task bundle from /tasks/my-day. Scope defaults to "mine" per
  // spec — every admin can toggle to Team or All + filter by member.
  const [tasks, setTasks] = useState({ overdue: [], due_today: [], due_this_week: [], later: [], unscheduled: [] });
  const [scope, setScope] = useState("mine");
  const [assignedFilter, setAssignedFilter] = useState("");
  const me = (user?.email || "").toLowerCase();
  const H = { headers: { "X-Admin-Password": pw } };

  // Save Filter Presets — localStorage-persisted named views so recruiters
  // can bookmark "Just Priya's overdue" or "Everyone this week" and load
  // them with one click for daily standups.
  const PRESET_KEY = "ditch_my_day_presets";
  const readPresets = () => {
    try { return JSON.parse(localStorage.getItem(PRESET_KEY) || "[]"); }
    catch { return []; }
  };
  const [presets, setPresets] = useState(readPresets);
  const [presetName, setPresetName] = useState("");
  const savePreset = () => {
    const name = presetName.trim();
    if (!name) { toast.error("Give the preset a name first."); return; }
    const next = [...presets.filter((p) => p.name !== name), { name, scope, assignedFilter }];
    localStorage.setItem(PRESET_KEY, JSON.stringify(next));
    setPresets(next);
    setPresetName("");
    toast.success(`Saved preset "${name}".`);
  };
  const loadPreset = (p) => { setScope(p.scope); setAssignedFilter(p.assignedFilter || ""); };
  const deletePreset = (name) => {
    const next = presets.filter((p) => p.name !== name);
    localStorage.setItem(PRESET_KEY, JSON.stringify(next));
    setPresets(next);
  };

  const load = useCallback(async () => {
    if (!me) return;
    setBusy(true);
    try {
      const r = await axios.get(`${BASE}/hunter/my-day?me=${encodeURIComponent(me)}`, H);
      setData(r.data);
    } catch (e) { toast.error("My Day load failed"); }
    finally { setBusy(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  const loadTasks = useCallback(async () => {
    if (!me) return;
    const params = new URLSearchParams({ scope, me });
    if (assignedFilter) params.set("assigned_to", assignedFilter);
    try {
      const r = await axios.get(`${BASE}/tasks/my-day?${params.toString()}`, H);
      setTasks(r.data);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, scope, assignedFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Fail-safe digest for missed hand-off emails — pulls records
  // recently assigned to me so nothing slips off the radar. Sep 2026.
  const loadAssignments = useCallback(async () => {
    if (!me) return;
    try {
      const r = await axios.get(`${BASE}/my-assignments?me=${encodeURIComponent(me)}&days=14`, H);
      setAssignments(r.data?.assignments || []);
    } catch { /* silent — surface as empty */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);
  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  const dismissAssignment = (row) => {
    const key = `${row.entity_type}:${row.entity_id}`;
    const next = new Set(dismissed);
    next.add(key);
    setDismissed(next);
    try { localStorage.setItem("ditch_dismissed_assignments", JSON.stringify([...next])); } catch {}
  };
  const visibleAssignments = assignments.filter(
    (r) => !dismissed.has(`${r.entity_type}:${r.entity_id}`)
  );

  const completeTask = async (id) => {
    try {
      await axios.patch(`${BASE}/tasks/${id}`, { status: "done" }, { headers: { "X-Admin-Password": pw, "X-Admin-User": me } });
      loadTasks();
    } catch { toast.error("Couldn't update task."); }
  };

  const moveTask = async (task, target, positionOverride) => {
    // target = "overdue" | "today" | "week" | "month" | null (keep column)
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    let due = "";
    if (target === "overdue") { const d = new Date(today); d.setDate(d.getDate() - 1); due = iso(d); }
    else if (target === "today") { due = iso(today); }
    else if (target === "week")  { const d = new Date(today); d.setDate(d.getDate() + 3); due = iso(d); }
    else if (target === "month") {
      // Mid-point of the remaining month, or last day if we're already past mid.
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const midOffset = Math.max(8, Math.floor((monthEnd.getDate() - today.getDate()) / 2));
      const d = new Date(today); d.setDate(d.getDate() + midOffset);
      due = iso(d > monthEnd ? monthEnd : d);
    }
    const patch = {};
    if (due && due !== task.due_date) patch.due_date = due;
    if (typeof positionOverride === "number" && isFinite(positionOverride)) {
      patch.position = positionOverride;
    }
    if (Object.keys(patch).length === 0) return;
    try {
      await axios.patch(`${BASE}/tasks/${task.id}`, patch,
        { headers: { "X-Admin-Password": pw, "X-Admin-User": me } });
      loadTasks();
    } catch { toast.error("Couldn't move task."); }
  };

  // Compute a fractional position that lands `beforeTask` immediately
  // before `beforeTask` within its column, or at the bottom of `colRows`
  // when `beforeTask` is null. Uses midpoint between neighbours so we
  // never have to renumber siblings.
  const _pos = (t) => (typeof t?.position === "number" ? t.position : 0);
  const computeDropPosition = (colRows, beforeTaskId, draggedId) => {
    const rows = colRows.filter((r) => r.id !== draggedId);
    if (rows.length === 0) return Date.now();
    if (!beforeTaskId) {
      const last = rows[rows.length - 1];
      return _pos(last) + 1000;
    }
    const idx = rows.findIndex((r) => r.id === beforeTaskId);
    if (idx < 0) return _pos(rows[rows.length - 1]) + 1000;
    if (idx === 0) return _pos(rows[0]) - 1000;
    const prev = rows[idx - 1];
    const next = rows[idx];
    return (_pos(prev) + _pos(next)) / 2;
  };
  const [dragId, setDragId] = useState(null);
  const [drawerId, setDrawerId] = useState(null);
  const allOpenTasks = [
    ...(tasks.overdue || []),
    ...(tasks.due_today || []),
    ...(tasks.due_this_week || []),
    ...(tasks.later || []),
    ...(tasks.unscheduled || []),
  ];
  const drawerTask = allOpenTasks.find((t) => t.id === drawerId);

  const snooze = async (eid, days) => {
    const dt = new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
    try {
      await axios.patch(`${BASE}/employers/${eid}`, { next_follow_up_at: dt }, H);
      toast.success(`Snoozed +${days}d → ${dt}`);
      load();
    } catch { toast.error("Snooze failed"); }
  };

  if (!me) return (
    <div className="bg-white rounded-2xl border border-[#E4E7EA] p-8 text-center">
      <p className="text-sm text-[#6F8FA8]">Sign in with Google to see your personal My Day view.</p>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="my-day-panel">
      {/* Live board — active employers with roles open right now */}
      <div className="bg-white rounded-2xl border border-[#E4E7EA] p-6" data-testid="my-day-live-board">
        <LiveBoard pw={pw} onOpenEmployer={onOpenEmployer} compact />
      </div>

      {/* Standalone task creator — recruiters need to punch in a task
          from My Day without hopping into a candidate/employer first. */}
      <MyDayTaskCreator pw={pw} me={me} onSaved={() => { loadTasks(); loadAssignments(); }} />

      {/* ── Newly assigned to you — fail-safe for the hand-off email
          alerts. Each row shows a circle badge with the open-task
          count so recruiters can see at a glance what they owe on the
          record. Dismissals persist in localStorage. */}
      <div
        className="bg-white rounded-2xl border border-[#E4E7EA] p-4"
        data-testid="my-day-newly-assigned"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-[#0D2F4D]">
              Newly assigned to you
            </p>
            {visibleAssignments.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 rounded-full bg-[#C6D21F] text-[#0D2F4D] text-[11px] font-extrabold px-1.5">
                {visibleAssignments.length}
              </span>
            )}
          </div>
          <button
            onClick={loadAssignments}
            data-testid="my-day-newly-assigned-refresh"
            className="text-[11px] text-[#6F8FA8] hover:text-[#0D2F4D] font-semibold"
          >
            Refresh
          </button>
        </div>
        {visibleAssignments.length === 0 ? (
          <p className="text-xs text-[#94A3B8]" data-testid="my-day-newly-assigned-empty">
            All caught up — no unseen hand-offs in the last 14 days.
          </p>
        ) : (
          <ul className="divide-y divide-[#F1F3F5]" data-testid="my-day-newly-assigned-list">
            {visibleAssignments.map((row) => {
              const key = `${row.entity_type}:${row.entity_id}`;
              const count = row.open_tasks_count || 0;
              const circleColor = count === 0
                ? "bg-[#F1F3F5] text-[#94A3B8]"
                : count >= 3
                  ? "bg-[#F5C6C6] text-[#0D2F4D]"
                  : "bg-[#F5E6C8] text-[#0D2F4D]";
              return (
                <li
                  key={key}
                  data-testid={`newly-assigned-${row.entity_type}-${row.entity_id}`}
                  className="flex items-center gap-3 py-2.5"
                >
                  <span
                    data-testid={`newly-assigned-circle-${row.entity_id}`}
                    className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full text-[13px] font-extrabold ${circleColor}`}
                    title={count === 0 ? "No open tasks yet" : `${count} open task${count === 1 ? "" : "s"}`}
                  >
                    {count}
                  </span>
                  <button
                    onClick={() => {
                      if (row.entity_type === "employer" && onOpenEmployer) {
                        onOpenEmployer(row.entity_id);
                      }
                    }}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`newly-assigned-open-${row.entity_id}`}
                  >
                    <p className="text-sm font-bold text-[#0D2F4D] truncate">{row.label}</p>
                    <p className="text-[11px] text-[#6F8FA8] truncate">
                      {row.entity_type}
                      {row.status ? ` · ${row.status}` : ""}
                      {row.location ? ` · ${row.location}` : ""}
                    </p>
                  </button>
                  <button
                    onClick={() => dismissAssignment(row)}
                    data-testid={`newly-assigned-dismiss-${row.entity_id}`}
                    className="text-[11px] text-[#6F8FA8] hover:text-[#0D2F4D] px-2 py-1 rounded-full hover:bg-[#F8F9FB]"
                  >
                    Dismiss
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Scope toggle — spec says default 'Mine', with Team + All options + assignee filter. */}
      <div className="bg-white rounded-2xl border border-[#E4E7EA] p-4 flex flex-wrap items-center gap-3" data-testid="my-day-scope">
        <p className="text-[10px] uppercase tracking-[0.22em] font-black text-[#0D2F4D]">Show tasks</p>
        <div className="flex rounded-full bg-[#FAFAF7] border border-[#E4E7EA] p-0.5">
          {[
            { v: "mine", l: "Mine" },
            { v: "team", l: "Team" },
            { v: "all",  l: "All" },
          ].map((o) => (
            <button
              key={o.v}
              onClick={() => { setScope(o.v); setAssignedFilter(""); }}
              data-testid={`my-day-scope-${o.v}`}
              className={`text-[11px] font-bold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full transition-all ${
                scope === o.v ? "bg-[#0D2F4D] text-[#C6D21F]" : "text-[#6F8FA8] hover:text-[#0D2F4D]"
              }`}
            >{o.l}</button>
          ))}
        </div>
        <select
          value={assignedFilter}
          onChange={(e) => setAssignedFilter(e.target.value)}
          data-testid="my-day-filter-assignee"
          className="text-xs bg-white border border-[#D6DCE3] rounded-full px-3 py-1.5 focus:outline-none focus:border-[#0D2F4D]"
        >
          <option value="">Everyone in scope</option>
          <option value="cara@theditchcollective.co.nz">Just Cara</option>
          <option value="priya@theditchcollective.co.nz">Just Priya</option>
          <option value="laura@theditchcollective.co.nz">Just Laura</option>
          <option value="holly@theditchcollective.co.nz">Just Holly</option>
        </select>
        <span className="text-[11px] text-[#94A3B8] ml-auto">
          {(tasks.counts?.overdue ?? 0) + (tasks.counts?.due_today ?? 0) + (tasks.counts?.due_this_week ?? 0)} open in scope
        </span>
      </div>

      {/* Filter presets — save + one-click load for standup views */}
      <div className="bg-white rounded-2xl border border-[#E4E7EA] p-3 flex flex-wrap items-center gap-2" data-testid="my-day-presets">
        <span className="text-[10px] uppercase tracking-[0.22em] font-black text-[#0D2F4D] mr-1">Presets</span>
        {presets.length === 0 && (
          <span className="text-[11px] text-[#94A3B8]">Save your current filters as a named view.</span>
        )}
        {presets.map((p) => (
          <span key={p.name} className="inline-flex items-center gap-1 rounded-full bg-[#FAFAF7] border border-[#E4E7EA] text-[11px]">
            <button
              onClick={() => loadPreset(p)}
              data-testid={`my-day-preset-load-${p.name.replace(/[^a-z0-9]/gi,'-').toLowerCase()}`}
              className="pl-3 pr-1 py-1 text-[#0D2F4D] hover:text-[#1a4063] font-semibold"
            >{p.name}</button>
            <button
              onClick={() => deletePreset(p.name)}
              className="pr-2 pl-1 text-[#94A3B8] hover:text-red-500"
              title="Remove"
            >×</button>
          </span>
        ))}
        <input
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && savePreset()}
          placeholder="Name this view…"
          data-testid="my-day-preset-name"
          className="text-xs bg-white border border-[#D6DCE3] rounded-full px-3 py-1 focus:outline-none focus:border-[#0D2F4D] flex-1 min-w-[140px] max-w-[220px]"
        />
        <button
          onClick={savePreset}
          data-testid="my-day-preset-save"
          className="text-[10px] font-bold uppercase tracking-[0.12em] px-3 py-1 rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#d3df34]"
        >Save preset</button>
      </div>

      {/* Quick assignee chips — one-click pivot to a teammate's board */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="my-day-avatar-chips">
        <span className="text-[10px] uppercase tracking-[0.22em] font-black text-[#0D2F4D] mr-1">Whose board</span>
        {[
          { email: "", label: "Everyone", color: "bg-[#0D2F4D] text-[#C6D21F]" },
          ...TEAM_OPTIONS,
        ].map((t) => {
          const active = assignedFilter === t.email;
          return (
            <button
              key={t.email || "all"}
              onClick={() => setAssignedFilter(assignedFilter === t.email && t.email ? "" : t.email)}
              data-testid={`my-day-chip-${t.email ? t.email.split("@")[0] : "everyone"}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold transition-all ${
                active
                  ? "bg-[#0D2F4D] text-[#C6D21F] shadow-sm"
                  : "bg-white border border-[#D6DCE3] text-[#0D2F4D] hover:border-[#0D2F4D]"
              }`}
            >
              <span className={`h-4 w-4 rounded-full inline-flex items-center justify-center text-[9px] font-bold ${
                active ? "bg-[#C6D21F] text-[#0D2F4D]" : "bg-[#0D2F4D] text-[#C6D21F]"
              }`}>{(t.label || "E")[0]}</span>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Kanban board — five columns of "stuff we need to do" */}
      {(() => {
        // Compute end-of-month for the "this month" bucket. Tasks whose
        // due lands after this week but still within the current calendar
        // month land here; everything further out drops into… nothing
        // (kept off the board so the view stays actionable).
        const today = new Date();
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        const inThisMonth = (t) => {
          if (!t.due_date) return false;
          try {
            const d = new Date(t.due_date + "T00:00:00");
            return d > today && d <= monthEnd;
          } catch { return false; }
        };
        const later = (tasks.later || []).filter(inThisMonth);
        const allOpen = [
          ...(tasks.overdue || []),
          ...(tasks.due_today || []),
          ...(tasks.due_this_week || []),
          ...(tasks.later || []),
          ...(tasks.unscheduled || []),
        ];
        const recurring = allOpen.filter((t) => t.recurrence && t.recurrence !== "none");

        const columns = [
          { key: "overdue",       label: "Overdue",   rows: tasks.overdue || [],       tone: "border-red-300 bg-red-50/50",         header: "text-red-600",   quickAdd: true  },
          { key: "due_today",     label: "Today",     rows: tasks.due_today || [],     tone: "border-[#C6D21F]/70 bg-[#FDFCE8]",    header: "text-[#0D2F4D]", quickAdd: true  },
          { key: "due_this_week", label: "This week", rows: [...(tasks.due_this_week || []), ...(tasks.unscheduled || [])], tone: "border-[#E4E7EA] bg-white", header: "text-[#0D2F4D]", quickAdd: true },
          { key: "this_month",    label: "This month", rows: later,                    tone: "border-[#D6DCE3] bg-[#FAFAF7]",       header: "text-[#0D2F4D]", quickAdd: true  },
          { key: "recurring",     label: "Recurring", rows: recurring,                 tone: "border-[#0D2F4D]/30 bg-[#F5F8FB]",    header: "text-[#0D2F4D]", quickAdd: false },
        ];
        return (
          <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-5" data-testid="my-day-board">
            {columns.map((col) => (
              <div key={col.key}
                   onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ring-2", "ring-[#0D2F4D]/40"); }}
                   onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-[#0D2F4D]/40")}
                   onDrop={(e) => {
                     e.preventDefault();
                     e.currentTarget.classList.remove("ring-2", "ring-[#0D2F4D]/40");
                     const id = e.dataTransfer.getData("text/plain") || dragId;
                     if (!id) return;
                     const task = allOpen.find((t) => t.id === id);
                     if (!task) { setDragId(null); return; }
                     if (col.key === "recurring") { setDragId(null); return; }  // view-only
                     const targetMap = {
                       overdue: "overdue", due_today: "today", due_this_week: "week",
                       this_month: "month",
                     };
                     const nextPos = computeDropPosition(col.rows, null, id);
                     moveTask(task, targetMap[col.key], nextPos);
                     setDragId(null);
                   }}
                   className={`rounded-2xl border ${col.tone} p-4 min-h-[220px] transition-all`}
                   data-testid={`my-day-column-${col.key}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <p className={`text-[10px] uppercase tracking-[0.22em] font-black ${col.header}`}>{col.label}</p>
                  <span className="text-[11px] text-[#6F8FA8]">{col.rows.length}</span>
                </div>
                {col.rows.length === 0 ? (
                  <p className="text-xs text-[#94A3B8] italic">Nothing here yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {col.rows.map((t) => (
                      <li key={t.id} data-testid={`my-day-task-${t.id}`}
                          draggable
                          onDragStart={(e) => {
                            setDragId(t.id);
                            e.dataTransfer.setData("text/plain", t.id);
                            e.dataTransfer.effectAllowed = "move";
                          }}
                          onDragEnd={() => setDragId(null)}
                          onDragOver={(e) => {
                            // Card-level drop target — lets recruiters
                            // reorder within a column (position field).
                            if (col.key === "recurring") return;
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onDrop={(e) => {
                            if (col.key === "recurring") return;
                            e.preventDefault();
                            e.stopPropagation();
                            const id = e.dataTransfer.getData("text/plain") || dragId;
                            if (!id || id === t.id) { setDragId(null); return; }
                            const task = allOpen.find((x) => x.id === id);
                            if (!task) { setDragId(null); return; }
                            const targetMap = {
                              overdue: "overdue", due_today: "today",
                              due_this_week: "week", this_month: "month",
                            };
                            const nextPos = computeDropPosition(col.rows, t.id, id);
                            moveTask(task, targetMap[col.key], nextPos);
                            setDragId(null);
                          }}
                          onClick={(e) => {
                            // Ignore clicks on the tick checkbox — that's a
                            // "mark done" gesture, not a "peek" gesture.
                            if (e.target.closest("[data-testid^='my-day-task-done-']")) return;
                            setDrawerId(t.id);
                          }}
                          className={`group p-3 rounded-lg bg-white/80 border border-white hover:bg-white hover:shadow-sm transition-all cursor-grab active:cursor-grabbing ${dragId === t.id ? "opacity-40" : ""}`}>                        <div className="flex items-start gap-2">
                          <button
                            onClick={() => completeTask(t.id)}
                            data-testid={`my-day-task-done-${t.id}`}
                            className="mt-0.5 h-4 w-4 rounded border border-[#D6DCE3] hover:border-[#0D2F4D] shrink-0"
                            title="Mark done"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-[#0D2F4D] leading-snug break-words">{t.title}</p>
                            <p className="text-[11px] text-[#6F8FA8] mt-1 flex items-center gap-2 flex-wrap">
                              {t.due_date && <span>{t.due_date}</span>}
                              <span className="text-[#0D2F4D] font-semibold">{(t.assigned_to || "unassigned").split("@")[0]}</span>
                              {t.recurrence && t.recurrence !== "none" && (
                                <span className="inline-flex items-center gap-1 text-[#0D2F4D]" title={`Repeats ${t.recurrence}`}>
                                  <Repeat size={9}/> {t.recurrence}
                                </span>
                              )}
                              {t.entity_type && <span className="text-[#94A3B8]">· {t.entity_type}</span>}
                            </p>
                            {t.notes && (
                              <p className="text-[11px] text-[#94A3B8] mt-1 line-clamp-2 whitespace-pre-wrap leading-snug">{t.notes}</p>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <ColumnQuickAdd
                  pw={pw}
                  me={me}
                  columnKey={col.key}
                  defaultAssignee={assignedFilter}
                  onCreated={loadTasks}
                  disabled={col.quickAdd === false}
                />

              </div>
            ))}
          </div>
        );
      })()}

      {/* Card detail drawer */}
      {drawerTask && (
        <TaskDetailDrawer
          task={drawerTask}
          pw={pw}
          me={me}
          onClose={() => setDrawerId(null)}
          onChanged={loadTasks}
        />
      )}

      {/* Reply alerts banner */}
      {data?.unread_replies?.length > 0 && (
        <div className="bg-gradient-to-r from-[#C6D21F] to-[#dae63a] rounded-2xl border border-[#0D2F4D]/20 p-5 shadow-sm" data-testid="my-day-reply-banner">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-[#0D2F4D] text-[#C6D21F] w-10 h-10 flex items-center justify-center font-bold text-lg shrink-0">
              {data.unread_replies.length}
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#0D2F4D]">New reply · last 72h</p>
              <p className="text-sm text-[#0D2F4D] font-semibold">You've got {data.unread_replies.length} new employer {data.unread_replies.length === 1 ? "reply" : "replies"} waiting for a follow-up.</p>
              <div className="mt-3 space-y-1.5">
                {data.unread_replies.slice(0, 4).map((r, i) => {
                  const firstName = (r.from || "").split("@")[0].split(".")[0] || "there";
                  const draftSubject = r.subject && r.subject.toLowerCase().startsWith("re:")
                    ? r.subject
                    : `Re: ${r.subject || "our conversation"}`;
                  const draftBody =
                    `Hi ${firstName.charAt(0).toUpperCase() + firstName.slice(1)},\n\n` +
                    `Thanks for getting back to me — appreciate the reply.\n\n` +
                    `[Write a personal follow-up here — you've got the context from their message.]\n\n` +
                    `Let me know a time that suits and I'll lock it in.\n\n` +
                    `Cheers,\n${(user?.name || "").split(" ")[0] || ""}\n\n` +
                    `---\n> ${(r.snippet || "").replace(/\n/g, "\n> ")}`;
                  return (
                    <div key={`${r.employer_id}-${r.subject || i}`} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/60 hover:bg-white transition-colors" data-testid={`my-day-reply-${i}`}>
                      <button
                        onClick={() => onOpenEmployer(r.employer_id)}
                        className="flex items-center gap-3 flex-1 text-left min-w-0"
                      >
                        <span className="text-sm font-bold text-[#0D2F4D] truncate w-40 shrink-0">{r.company_name}</span>
                        <span className="text-xs text-[#0D2F4D]/70 truncate flex-1">{r.subject || "(no subject)"} — {r.snippet}</span>
                        <span className="text-[10px] text-[#0D2F4D]/60 whitespace-nowrap">{new Date(r.at).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short'})}</span>
                      </button>
                      <button
                        onClick={() => {
                          sessionStorage.setItem(
                            `ditch_reply_draft_${r.employer_id}`,
                            JSON.stringify({ subject: draftSubject, body: draftBody }),
                          );
                          onOpenEmployer(r.employer_id);
                        }}
                        data-testid={`my-day-reply-now-${i}`}
                        className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90 whitespace-nowrap shrink-0"
                        title="Open the branded composer with the thread pre-quoted and a suggested opener"
                      >Reply now →</button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-red-200 p-5" data-testid="my-day-overdue">
          <p className="text-[10px] uppercase tracking-widest font-bold text-red-600 mb-1">Overdue</p>
          <p className="font-display text-4xl font-extrabold text-red-600">{data?.counts.overdue ?? 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#C6D21F]/50 p-5" data-testid="my-day-today">
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#0D2F4D] mb-1">Due today</p>
          <p className="font-display text-4xl font-extrabold text-[#0D2F4D]">{data?.counts.due_today ?? 0}</p>
        </div>
      </div>

      {[
        ["OVERDUE", data?.overdue || [], "border-red-200"],
        ["DUE TODAY", data?.due_today || [], "border-[#C6D21F]/40"],
      ].map(([label, rows, border]) => rows.length > 0 && (
        <div key={label} className={`bg-white rounded-2xl border ${border} overflow-hidden`}>
          <div className="px-5 py-3 border-b border-[#F2F2EF] bg-[#FAFAF7]">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#0D2F4D]">{label} · {rows.length}</p>
          </div>
          <div className="divide-y divide-[#F2F2EF]">
            {rows.map(r => (
              <div key={r.employer_id} className="flex items-center gap-3 px-5 py-3 hover:bg-[#FAFAF7]" data-testid={`my-day-row-${r.employer_id}`}>
                <button
                  onClick={() => onOpenEmployer(r.employer_id)}
                  className="flex-1 text-left"
                >
                  <p className="font-semibold text-[#0D2F4D] text-sm">{r.company_name}</p>
                  <p className="text-xs text-[#6F8FA8]">{r.contact_name || r.contact_email || "—"} · {r.area || "—"}</p>
                </button>
                <span className={`text-xs font-mono ${r.days_from_today < 0 ? "text-red-600 font-bold" : "text-[#0D2F4D]"}`}>
                  {r.days_from_today < 0 ? `${Math.abs(r.days_from_today)}d overdue` : "today"}
                </span>
                {[1, 3, 7].map(d => (
                  <button
                    key={d}
                    onClick={() => snooze(r.employer_id, d)}
                    data-testid={`my-day-snooze-${r.employer_id}-${d}d`}
                    className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-white text-[#0D2F4D] border border-[#D6DCE3] hover:bg-[#F7F9E8] hover:border-[#0D2F4D]"
                  >+{d}d</button>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}

      {!busy && (data?.counts.overdue ?? 0) === 0 && (data?.counts.due_today ?? 0) === 0 && (data?.unread_replies?.length ?? 0) === 0 && (tasks.counts?.overdue ?? 0) === 0 && (tasks.counts?.due_today ?? 0) === 0 && (tasks.counts?.due_this_week ?? 0) === 0 && (
        <div className="bg-white rounded-2xl border border-[#E4E7EA] p-10 text-center">
          <p className="text-2xl font-display font-extrabold text-[#0D2F4D]">Inbox zero.</p>
          <p className="text-sm text-[#6F8FA8] mt-2">Nothing overdue and no calls to make today. Go hunt some new accounts.</p>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
 * PulseTicker — fixed bottom-right sidebar showing live company activity.
 * ═══════════════════════════════════════════════════════════════════════ */
export function PulseTicker({ pw }) {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const H = { headers: { "X-Admin-Password": pw } };

  useEffect(() => {
    if (!pw) return;
    let cancelled = false;
    const load = () => {
      axios.get(`${BASE}/pulse?limit=30`, H)
        .then(r => { if (!cancelled) setItems(r.data.items || []); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 20000);   // refresh every 20s
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pw]);

  const KIND_STYLES = {
    send:          { label: "SEND",   dot: "bg-emerald-500" },
    reply:         { label: "REPLY",  dot: "bg-blue-500"    },
    call:          { label: "CALL",   dot: "bg-purple-500"  },
    meeting:       { label: "MEET",   dot: "bg-purple-500"  },
    comms:         { label: "NOTE",   dot: "bg-slate-400"   },
    new_candidate: { label: "NEW",    dot: "bg-[#C6D21F]"   },
    new_employer:  { label: "NEW",    dot: "bg-[#C6D21F]"   },
    new_lead:      { label: "LEAD",   dot: "bg-[#C6D21F]"   },
  };

  if (!items.length) return null;

  return (
    <div
      className={cx(
        "fixed bottom-0 right-4 z-40 bg-[#0D2F4D] text-white rounded-t-2xl shadow-2xl border border-white/10 transition-all",
        expanded ? "w-[360px] h-[440px]" : "w-[360px] h-[52px]"
      )}
      data-testid="pulse-ticker"
    >
      <button
        onClick={() => setExpanded(x => !x)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left"
        data-testid="pulse-toggle"
      >
        <span className="inline-block w-2 h-2 rounded-full bg-[#C6D21F] animate-pulse"/>
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#C6D21F]">Pulse ticker</span>
        <span className="text-xs text-white/70 ml-auto flex-1 truncate">
          {!expanded && items[0]?.line}
        </span>
        <span className="text-[10px] font-bold">{expanded ? "▾" : "▴"}</span>
      </button>
      {expanded && (
        <div className="h-[calc(100%-52px)] overflow-y-auto px-4 py-2 space-y-1.5" data-testid="pulse-list">
          {items.map((it, i) => {
            const style = KIND_STYLES[it.kind] || KIND_STYLES.comms;
            return (
              <div key={`${it.kind}-${it.line}`} className="flex items-start gap-2 py-1.5 border-b border-white/5 last:border-b-0" data-testid={`pulse-item-${i}`}>
                <span className={cx("inline-block w-1.5 h-1.5 rounded-full mt-1.5 shrink-0", style.dot)}/>
                <div className="flex-1 min-w-0">
                  <span className="text-[9px] uppercase tracking-widest font-bold text-[#C6D21F] mr-2">{style.label}</span>
                  <span className="text-xs text-white leading-tight">{it.line}</span>
                  <p className="text-[10px] text-white/40 mt-0.5">{it.at ? new Date(it.at).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short'}) : ""}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function EmployerImportDialog({ open, onClose, onImported, pw }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!file) { toast.error("Pick a CSV first."); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${BASE}/employers/import`, fd, {
        headers: { "X-Admin-Password": pw, "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`Imported ${r.data.inserted} new · ${r.data.updated} updated · ${r.data.skipped} skipped`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="employer-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold text-[#0D2F4D]">Hunter CSV import</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <p className="text-[#334155]">
            Upload a spreadsheet of prospects. Column headers matched case-insensitively:
          </p>
          <div className="text-[11px] font-mono text-[#6F8FA8] bg-[#F8F9FB] rounded p-3 leading-relaxed">
            company, email, first name, last name, phone, website,<br/>
            industry, location, <b className="text-[#0D2F4D]">area</b>, <b className="text-[#0D2F4D]">assigned to</b>, source,<br/>
            hiring for, notes
          </div>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={e => setFile(e.target.files?.[0] || null)}
            data-testid="employer-import-file"
            className="block w-full text-sm text-[#0D2F4D] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-bold file:bg-[#0D2F4D] file:text-[#C6D21F] hover:file:bg-[#0D2F4D]/90"
          />
          {result && (
            <div className="bg-[#F7F9E8] border border-[#C6D21F]/40 rounded p-3 text-xs font-mono text-[#0D2F4D]">
              <div><b>Inserted:</b> {result.inserted} · <b>Updated:</b> {result.updated} · <b>Skipped:</b> {result.skipped}</div>
              {Object.keys(result.per_area || {}).length > 0 && (
                <div className="mt-2 pt-2 border-t border-[#C6D21F]/40">
                  <b>By area:</b> {Object.entries(result.per_area).map(([a, n]) => `${a} · ${n}`).join(" · ")}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="employer-import-cancel">Close</Button>
          <Button
            className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]"
            onClick={submit}
            disabled={busy || !file}
            data-testid="employer-import-submit"
          >
            <Upload className="h-4 w-4 mr-1"/>{busy ? "Importing…" : "Upload CSV"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─────────────────────────── Abandoned Signups ─────────────────────────── */
function AbandonedSignupsTable({ rows, pw, onRefresh, onOpen }) {
  const [running, setRunning] = useState(false);

  const fireAll = async () => {
    if (!window.confirm("Send the Incomplete Profile Nudge to every abandoned signup that hasn't been nudged yet?")) return;
    setRunning(true);
    try {
      const r = await axios.post(
        `${BASE}/candidates/abandoned/run-now`,
        {},
        { headers: { "X-Admin-Password": pw } },
      );
      const nudged = (r.data.nudged || []).length;
      const failed = (r.data.failed || []).length;
      toast.success(`Nudge sweep complete — sent ${nudged}${failed ? `, ${failed} failed` : ""}.`);
      onRefresh?.();
    } catch (e) {
      toast.error(`Nudge run failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setRunning(false); }
  };

  const stale = rows.filter((r) => !r.incomplete_profile_nudge_sent_at);
  const nudged = rows.filter((r) => r.incomplete_profile_nudge_sent_at);

  return (
    <div className="space-y-6" data-testid="abandoned-signups-panel">
      <div className="bg-white rounded-2xl border border-[#E4E7EA] p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px]">
            <h2 className="font-display text-2xl font-extrabold text-[#0D2F4D]">Abandoned Signups</h2>
            <p className="text-sm text-[#6F8FA8] mt-1">
              Candidates who created an account on Careers ≥24 hours ago and haven't uploaded a CV. Nudge them once with a branded "need a hand finishing?" email.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest font-black text-[#6F8FA8]">Waiting for nudge</div>
            <div className="text-3xl font-extrabold text-[#0D2F4D]">{stale.length}</div>
          </div>
          <Button
            onClick={fireAll}
            disabled={running || stale.length === 0}
            className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]"
            data-testid="abandoned-run-nudge"
          >
            <Send className="h-4 w-4 mr-1.5" />
            {running ? "Sending…" : "Send nudge now"}
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E4E7EA] p-10 text-center">
          <UserX className="mx-auto h-8 w-8 text-[#6F8FA8]" />
          <p className="text-sm text-[#6F8FA8] mt-3">
            No abandoned signups right now. Every candidate created in the last 24 hours has either uploaded a CV or already been nudged.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E4E7EA] overflow-hidden">
          <table className="w-full text-sm" data-testid="abandoned-signups-table">
            <thead className="bg-[#FAFAF7] text-left text-[10px] uppercase tracking-widest font-black text-[#6F8FA8]">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Signed up</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F0EB]">
              {[...stale, ...nudged].map((c) => {
                const p = c.person || {};
                const name = `${p.first_name || ""} ${p.last_name || ""}`.trim() || "—";
                const nudgedAt = c.incomplete_profile_nudge_sent_at;
                return (
                  <tr key={c.id} className="hover:bg-[#FAFAF7]" data-testid={`abandoned-row-${c.id}`}>
                    <td className="px-4 py-3 text-[#0D2F4D] font-semibold">{name}</td>
                    <td className="px-4 py-3 text-[#33475F]">{p.email || "—"}</td>
                    <td className="px-4 py-3 text-[#6F8FA8]">
                      {c.created_at ? new Date(c.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {nudgedAt ? (
                        <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#F0F5EA] text-[#5A6D0F]">
                          Nudged {new Date(nudgedAt).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#FDF3E7] text-[#B0611F]">
                          Waiting
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => onOpen?.(c)}
                        className="text-[12px] font-bold text-[#0D2F4D] hover:underline inline-flex items-center gap-1"
                        data-testid={`abandoned-open-${c.id}`}
                      >
                        Open <ArrowRight size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



function EmployersTable({ rows, onOpen, pw, onBulkComplete, mode = "all" }) {
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [rowDeletingId, setRowDeletingId] = useState(null);
  const [promoting, setPromoting] = useState({});     // { [employerId]: true } while the toggle is in-flight
  const [assignee, setAssignee] = useState("");
  const [team, setTeam] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [journeys, setJourneys] = useState([]);
  const [journeyId, setJourneyId] = useState("");
  const [enrolling, setEnrolling] = useState(false);

  // "Email selected" — manual one-off email to employer contacts (e.g. invite
  // Outreach Targets to sign up to the employer portal).
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSender, setEmailSender] = useState("");
  const [emailTemplateKey, setEmailTemplateKey] = useState("");
  const [emailTemplatePreview, setEmailTemplatePreview] = useState("");
  const [emailTemplateHasBlocks, setEmailTemplateHasBlocks] = useState(false);
  const emailTargets = rows.filter((r) => selected.has(r.id));
  const emailWithAddr = emailTargets.filter((r) => r.contact?.email);
  const insertPortalInvite = () => {
    setEmailTemplateKey(""); setEmailTemplatePreview(""); setEmailTemplateHasBlocks(false);
    setEmailSubject("An easier way to hire across the ditch");
    setEmailBody(
      "Hi {{first_name}},\n\n" +
      "We help Australian employers hire pre-screened New Zealand talent — no visa sponsorship, and we handle the relocation.\n\n" +
      "See how we work: https://theditchcareers.com.au/employers\n\n" +
      "When you're ready, create your free employer account here: https://theditchcareers.com.au/signup/employer\n\n" +
      "Any questions, just reply to this email.\n\n" +
      "Cheers,\nThe Ditch Careers team"
    );
  };
  const sendBulkEmail = async () => {
    if (!emailSubject.trim() || (!emailBody.trim() && !emailTemplateKey)) { toast.error("Add a subject and a message."); return; }
    if (emailWithAddr.length === 0) { toast.error("None of the selected employers have a contact email."); return; }
    setEmailSending(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/employers/bulk-email`,
        { ids: emailWithAddr.map((c) => c.id), subject: emailSubject, body: emailBody, brand: "careers", sender_admin_email: emailSender || null, template_key: emailTemplateKey || null },
        { headers: { "X-Admin-Password": pw } },
      );
      const { sent = 0, failed = 0, skipped_no_email = 0 } = r.data || {};
      const parts = [`Sent ${sent}`];
      if (failed) parts.push(`${failed} failed`);
      if (skipped_no_email) parts.push(`${skipped_no_email} had no email`);
      if (sent > 0) toast.success(parts.join(" · ") + "."); else toast.error(parts.join(" · ") + ".");
      setEmailOpen(false); setEmailSubject(""); setEmailBody(""); setEmailTemplateKey(""); setEmailTemplatePreview(""); setEmailTemplateHasBlocks(false);
      setSelected(new Set());
      onBulkComplete?.();
    } catch (e) {
      toast.error(`Email failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setEmailSending(false); }
  };

  // Load the team roster once so the bulk-assign dropdown has real options.
  useEffect(() => {
    axios.get(`${BASE}/team`, { headers: { "X-Admin-Password": pw } })
      .then(r => setTeam(r.data.team || []))
      .catch(() => setTeam([]));
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/automations`, { headers: { "X-Admin-Password": pw } })
      .then(r => setJourneys((r.data.automations || []).filter(a => a.active)))
      .catch(() => setJourneys([]));
  }, [pw]);

  const bulkEnroll = async () => {
    if (selected.size === 0 || !journeyId) return;
    setEnrolling(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/employers/bulk-enroll`,
        { ids: Array.from(selected), automation_id: journeyId },
        { headers: { "X-Admin-Password": pw } },
      );
      const { enrolled, already_enrolled, no_email, automation_name } = r.data;
      let msg = `Enrolled ${enrolled} into "${automation_name}".`;
      if (already_enrolled) msg += ` ${already_enrolled} already in the journey.`;
      if (no_email) msg += ` ${no_email} skipped (no contact email).`;
      toast.success(msg);
      setSelected(new Set());
      onBulkComplete?.();
    } catch (e) {
      toast.error(`Bulk enrol failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setEnrolling(false); }
  };

  const bulkAssign = async () => {
    if (selected.size === 0) return;
    if (!assignee && !window.confirm(`Clear the assignment on ${selected.size} employer${selected.size === 1 ? "" : "s"}? They'll be moved to 'unassigned'.`)) return;
    setAssigning(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/employers/bulk-assign`,
        { ids: Array.from(selected), assigned_to: assignee },
        { headers: { "X-Admin-Password": pw } },
      );
      const { updated, requested, assigned_to } = r.data;
      const who = assigned_to ? (team.find(t => t.email === assigned_to)?.name || assigned_to.split("@")[0]) : "unassigned";
      toast.success(`Assigned ${updated} of ${requested} to ${who}.`);
      setSelected(new Set());
      onBulkComplete?.();
    } catch (e) {
      toast.error(`Bulk assign failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setAssigning(false); }
  };

  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => setSelected(prev => {
    if (prev.size === rows.length) return new Set();
    return new Set(rows.map(r => r.id));
  });
  const clearSel = () => setSelected(new Set());

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ok = window.confirm(
      `Delete ${selected.size} employer${selected.size === 1 ? "" : "s"}?\n\n` +
      `This removes each employer + their roles + notes + documents + timeline.\n` +
      `Their Company records are kept (so you don't lose historical company data).`,
    );
    if (!ok) return;
    setDeleting(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/employers/bulk-delete`,
        { ids: Array.from(selected) },
        { headers: { "X-Admin-Password": pw } },
      );
      const { count, requested } = r.data;
      toast.success(`Deleted ${count} of ${requested} employers.`);
      clearSel();
      onBulkComplete?.();
    } catch (e) {
      toast.error(`Bulk delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setDeleting(false); }
  };

  const deleteOne = async (e) => {
    const name = e?.company?.name || "this employer";
    if (!window.confirm(`Delete ${name}?\n\nRemoves the employer + their roles, notes, documents & timeline.\nThe Company record is kept for history. This can't be undone.`)) return;
    setRowDeletingId(e.id);
    try {
      await axios.delete(
        `${BASE}/employers/${e.id}`,
        { headers: { "X-Admin-Password": pw } },
      );
      toast.success(`Deleted ${name}.`);
      onBulkComplete?.();
    } catch (err) {
      toast.error(`Delete failed: ${err?.response?.data?.detail || err.message}`);
    } finally { setRowDeletingId(null); }
  };

  if (!rows.length) {
    const emptyCopy = mode === "active"
      ? "No Active Employers yet — promote a target from Outreach Targets once they sign on."
      : mode === "outreach"
        ? "No Outreach Targets in this view — import a CSV to start hunting."
        : "No employers yet — click New employer to add the first.";
    return (
      <div className="bg-white rounded-2xl p-12 text-center border border-[#E4E7EA]">
        <p className="text-[#6F8FA8]" data-testid="employers-empty">{emptyCopy}</p>
      </div>
    );
  }
  const allSelected = selected.size === rows.length && rows.length > 0;
  const someSelected = selected.size > 0 && !allSelected;
  return (
    <div className="space-y-3">
      {selected.size > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 bg-[#0D2F4D] text-white rounded-2xl px-5 py-3 border border-[#0D2F4D]"
          data-testid="employers-bulk-bar"
        >
          <span className="text-sm font-semibold">{selected.size} selected</span>

          {/* Bulk enrol — journey dropdown + go button */}
          <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto" data-testid="employers-bulk-enroll-controls">
            <label className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Journey</label>
            <select
              value={journeyId}
              onChange={(e) => setJourneyId(e.target.value)}
              data-testid="employers-bulk-enroll-select"
              className="text-xs font-semibold rounded-full bg-white text-[#0D2F4D] border border-white/20 px-3 py-1.5 focus:outline-none max-w-[220px]"
            >
              <option value="">{journeys.length ? "— Choose journey —" : "No active journeys"}</option>
              {journeys.map(j => (
                <option key={j.id} value={j.id}>{j.name}</option>
              ))}
            </select>
            <button
              onClick={bulkEnroll}
              disabled={enrolling || !journeyId}
              data-testid="employers-bulk-enroll"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-full bg-white text-[#0D2F4D] hover:bg-white/90 disabled:opacity-40 transition-colors"
            >
              {enrolling ? "Enrolling…" : `Enrol ${selected.size}`}
            </button>
          </div>

          {/* Bulk assign — dropdown + go button */}
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto" data-testid="employers-bulk-assign-controls">
            <label className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Assign to</label>
            <select
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              data-testid="employers-bulk-assign-select"
              className="text-xs font-semibold rounded-full bg-white text-[#0D2F4D] border border-white/20 px-3 py-1.5 focus:outline-none"
            >
              <option value="">— Unassigned —</option>
              {team.map(t => (
                <option key={t.email} value={t.email}>{t.name} · {t.email.split("@")[0]}</option>
              ))}
            </select>
            <button
              onClick={bulkAssign}
              disabled={assigning}
              data-testid="employers-bulk-assign"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#C6D21F]/90 disabled:opacity-40 transition-colors"
            >
              {assigning ? "Assigning…" : `Assign ${selected.size}`}
            </button>
          </div>

          <button
            onClick={bulkDelete}
            disabled={deleting}
            data-testid="employers-bulk-delete"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 transition-colors"
          >
            <Trash2 size={12}/> {deleting ? "Deleting…" : `Delete ${selected.size}`}
          </button>
          <button
            onClick={() => { setEmailSubject(""); setEmailBody(""); setEmailOpen(true); }}
            data-testid="employers-bulk-email"
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-full bg-white text-[#0D2F4D] hover:bg-white/90 transition-colors"
          >
            <Mail size={12}/> Email {selected.size}
          </button>
          <button
            onClick={clearSel}
            data-testid="employers-bulk-clear"
            className="text-xs text-white/70 hover:text-white"
          >
            Clear
          </button>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-[#E4E7EA] overflow-hidden">
        <table className="hidden w-full text-sm md:table">
          <thead className="bg-[#F2F2EF] text-[10px] uppercase tracking-widest text-[#6F8FA8]">
            <tr>
              <th className="px-3 py-3 w-[40px]">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => el && (el.indeterminate = someSelected)}
                  onChange={toggleAll}
                  data-testid="employers-select-all"
                  className="w-4 h-4 rounded cursor-pointer"
                />
              </th>
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Contact</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Area · Industry</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Assigned · Source</th>
              <th className="text-left px-4 py-3 hidden xl:table-cell">Hiring for</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr
                key={e.id}
                data-testid={WORKSPACE.employerRow(e.id)}
                className={cx(
                  "border-t border-[#F2F2EF] hover:bg-[#FAFAF7]",
                  selected.has(e.id) && "bg-[#C6D21F]/10",
                )}
              >
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(e.id)}
                    onChange={() => toggle(e.id)}
                    data-testid={`employer-select-${e.id}`}
                    className="w-4 h-4 rounded cursor-pointer"
                    onClick={(ev) => ev.stopPropagation()}
                  />
                </td>
                <td className="px-4 py-3">
                  <p className="font-semibold text-[#0D2F4D]">{e.company?.name}</p>
                  <p className="text-xs text-[#6F8FA8]">{e.company?.domain || "—"}</p>
                  {e.outreach_stage && (
                    <span
                      data-testid={`employer-outreach-stage-${e.id}`}
                      className="mt-1 inline-flex rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-800"
                    >
                      {e.outreach_stage}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <p className="text-[#0D2F4D]">
                    {e.contact?.first_name} {e.contact?.last_name}
                  </p>
                  <p className="text-xs text-[#6F8FA8]">{e.contact?.email}</p>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <p className="text-[#0D2F4D]">
                    {e.area ? <span className="inline-block bg-[#F7F9E8] text-[#0D2F4D] border border-[#C6D21F]/40 px-2 py-0.5 rounded-full text-[10px] font-bold">{e.area}</span> : <span className="text-[#94A3B8]">—</span>}
                  </p>
                  <p className="text-xs text-[#6F8FA8] mt-0.5">{e.industry || "—"}{e.location ? ` · ${e.location}` : ""}</p>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <p className="text-xs text-[#0D2F4D]">{e.assigned_to ? e.assigned_to.split("@")[0] : <span className="text-[#94A3B8]">unassigned</span>}</p>
                  <p className="text-[10px] text-[#6F8FA8]">{e.source || "—"}</p>
                </td>
                <td className="px-4 py-3 hidden xl:table-cell text-[#0D2F4D]">
                  {(e.hiring_for || []).slice(0, 2).join(", ") || "—"}
                  {(e.hiring_for || []).length > 2 && ` +${e.hiring_for.length - 2}`}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {e.has_active_subscription && (
                    <span
                      data-testid={`employer-paid-badge-${e.id}`}
                      title="Active Stripe/PayPal subscription"
                      className="text-[10px] font-extrabold tracking-widest px-2 py-0.5 rounded-full mr-2 bg-[#C6D21F] text-[#0D2F4D] border border-[#B0BC1A]"
                    >
                      PAID
                    </span>
                  )}
                  <button
                    onClick={async (ev) => {
                      ev.stopPropagation();
                      const promoteTo = (e.recruitment_status === "active_client") ? false : true;
                      setPromoting(p => ({ ...p, [e.id]: true }));
                      try {
                        await axios.post(
                          `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/employers/${e.id}/promote`,
                          { active_client: promoteTo },
                          { headers: { "X-Admin-Password": pw } },
                        );
                        toast.success(promoteTo ? "Promoted to Active Employer." : "Moved back to Outreach Targets.");
                        onBulkComplete?.();
                      } catch (err) {
                        toast.error(`Promote failed: ${err?.response?.data?.detail || err.message}`);
                      } finally {
                        setPromoting(p => { const n = { ...p }; delete n[e.id]; return n; });
                      }
                    }}
                    disabled={!!promoting[e.id]}
                    data-testid={`employer-promote-${e.id}`}
                    className={cx(
                      "text-[10px] font-bold px-2.5 py-1 rounded-full mr-2 inline-flex items-center gap-1 border transition-colors",
                      e.recruitment_status === "active_client"
                        ? "bg-white border-[#D6DCE3] text-[#6F8FA8] hover:border-[#0D2F4D]"
                        : "bg-[#C6D21F] border-[#C6D21F] text-[#0D2F4D] hover:bg-[#C6D21F]/80"
                    )}
                    title={e.recruitment_status === "active_client" ? "Demote back to Outreach" : "Promote to Active"}
                  >
                    {promoting[e.id]
                      ? "…"
                      : e.recruitment_status === "active_client" ? "↓ Demote" : "↑ Promote"}
                  </button>
                  <button
                    data-testid={`employer-delete-${e.id}`}
                    onClick={(ev) => { ev.stopPropagation(); deleteOne(e); }}
                    disabled={rowDeletingId === e.id}
                    title="Delete employer"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full p-1.5 mr-1 disabled:opacity-40 inline-flex items-center align-middle"
                  >
                    <Trash2 size={14} />
                  </button>
                  <a
                    href={`/admin?tab=${new URLSearchParams(window.location.search).get("tab") || "active-employers"}&employer=${e.id}`}
                    data-testid={WORKSPACE.employerOpen(e.id)}
                    onClick={(evt) => {
                      if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey || evt.button === 1) return;
                      evt.preventDefault();
                      onOpen(e);
                    }}
                    className="text-xs font-bold text-[#0D2F4D] hover:text-[#C6D21F] inline-flex items-center gap-1 no-underline"
                  >
                    Open <ArrowRight size={12} />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="divide-y divide-[#F2F2EF] md:hidden">
          <label className="flex items-center gap-3 bg-[#F2F2EF] px-4 py-3 text-xs font-bold uppercase tracking-widest text-[#6F8FA8]">
            <input type="checkbox" checked={allSelected} ref={(el) => el && (el.indeterminate = someSelected)} onChange={toggleAll} data-testid="employers-select-all-mobile" className="h-4 w-4 rounded"/>
            Select all employers
          </label>
          {rows.map((e) => (
            <div key={e.id} data-testid={`workspace-employer-mobile-${e.id}`} className={cx("p-4", selected.has(e.id) && "bg-[#C6D21F]/10")}>
              <div className="flex items-start gap-3">
                <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} data-testid={`employer-select-mobile-${e.id}`} className="mt-1 h-4 w-4 shrink-0 rounded"/>
                <div className="min-w-0 flex-1">
                  <p className="break-words font-semibold text-[#0D2F4D]">{e.company?.name || "Unnamed employer"}</p>
                  <p className="truncate text-xs text-[#6F8FA8]">{e.contact?.email || e.company?.domain || "No contact email"}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {e.outreach_stage && <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-800">{e.outreach_stage}</span>}
                    {e.has_active_subscription && <span className="rounded-full bg-[#C6D21F] px-2 py-0.5 text-[10px] font-bold text-[#0D2F4D]">PAID</span>}
                    {e.assigned_to && <span className="rounded-full bg-[#F2F2EF] px-2 py-0.5 text-[10px] text-[#6F8FA8]">{e.assigned_to.split("@")[0]}</span>}
                  </div>
                </div>
                <a href={`/admin?tab=${new URLSearchParams(window.location.search).get("tab") || "active-employers"}&employer=${e.id}`} data-testid={`workspace-employer-open-mobile-${e.id}`} onClick={(evt) => { evt.preventDefault(); onOpen(e); }} className="shrink-0 text-xs font-bold text-[#0D2F4D] no-underline">Open</a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Manual bulk email composer — invite employers (e.g. Outreach Targets) */}
      <Dialog open={emailOpen} onOpenChange={(o) => !o && setEmailOpen(false)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="employer-bulk-email-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={16}/> Email selected employers</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-[#6F8FA8]">
              One-off email to <strong>{emailWithAddr.length}</strong> employer{emailWithAddr.length === 1 ? "" : "s"}
              {emailTargets.length !== emailWithAddr.length && (
                <> · {emailTargets.length - emailWithAddr.length} skipped (no contact email)</>
              )}.
            </p>
            <button
              onClick={insertPortalInvite}
              data-testid="employer-email-portal-invite"
              className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-full bg-[#C6D21F]/20 text-[#0D2F4D] border border-[#C6D21F] hover:bg-[#C6D21F]/30"
            >
              Insert portal invite
            </button>
          </div>
          <EmailComposerExtras
            pw={pw}
            brand="careers"
            senderEmail={emailSender}
            onSender={setEmailSender}
            selectedKey={emailTemplateKey}
            onLoadTemplate={(t) => { setEmailTemplateKey(t?.key || ""); setEmailSubject(t?.subject || ""); setEmailBody(t?.body || ""); setEmailTemplatePreview(t?.preview_html || ""); setEmailTemplateHasBlocks(Boolean(t?.blocks?.length)); }}
          />
          <input
            type="text"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Subject"
            data-testid="employer-bulk-email-subject"
            className="w-full text-sm rounded-lg border border-[#D6DCE3] px-3 py-2 outline-none focus:border-[#0D2F4D]"
          />
          {emailTemplateHasBlocks && emailTemplatePreview ? (
            <div data-testid="employer-bulk-email-template-preview" className="space-y-2">
              <p className="text-xs font-semibold text-[#0D2F4D]">Full Email Hub design loaded. Links and layout will send exactly as previewed.</p>
              <iframe title="Selected employer email template preview" srcDoc={emailTemplatePreview} className="h-[420px] w-full rounded-lg border border-[#D6DCE3] bg-white" sandbox=""/>
            </div>
          ) : <textarea
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            placeholder={"Hi {{first_name}},\n\nWrite your message here… (tip: hit 'Insert portal invite')"}
            rows={9}
            data-testid="employer-bulk-email-body"
            className="w-full text-sm rounded-lg border border-[#D6DCE3] px-3 py-2 outline-none focus:border-[#0D2F4D] resize-y"
          />}
          <DialogFooter>
            <button onClick={() => setEmailOpen(false)} className="text-sm font-medium px-4 py-2 rounded-full text-[#6F8FA8] hover:text-[#0D2F4D]">Cancel</button>
            <button
              onClick={sendBulkEmail}
              disabled={emailSending || emailWithAddr.length === 0}
              data-testid="employer-bulk-email-send"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90 disabled:opacity-50"
            >
              <Mail size={13}/>{emailSending ? "Sending…" : `Send to ${emailWithAddr.length}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PeopleTable({ rows, pw }) {
  const [openId, setOpenId] = useState(null);
  const openRow = rows.find((r) => r.id === openId) || null;
  if (!rows.length) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center border border-[#E4E7EA]">
        <p className="text-[#6F8FA8]">No people match this view yet.</p>
      </div>
    );
  }
  return (
    <>
      <div className="bg-white rounded-2xl border border-[#E4E7EA] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F2F2EF] text-[10px] uppercase tracking-widest text-[#6F8FA8]">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Contexts</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Tags</th>
              <th className="text-right px-4 py-3 w-24"> </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} data-testid={WORKSPACE.personRow(p.id)} className="border-t border-[#F2F2EF] hover:bg-[#FAFAF7]">
                <td className="px-4 py-3 text-[#0D2F4D]">{p.first_name} {p.last_name}</td>
                <td className="px-4 py-3 text-[#0D2F4D]">{p.email}</td>
                <td className="px-4 py-3 hidden md:table-cell text-[#6F8FA8] text-xs">
                  {(p.contexts || []).join(", ") || "—"}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell"><TagPills tags={p.tags} /></td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setOpenId(p.id)}
                    data-testid={`person-payments-btn-${p.id}`}
                    className="text-xs font-bold px-3 py-1.5 rounded-full bg-white text-[#0D2F4D] border border-[#D6DCE3] hover:border-[#0D2F4D] inline-flex items-center gap-1"
                    title="Payments received"
                  >
                    Payments
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openRow && (
        <PersonPaymentsModal
          person={openRow}
          pw={pw}
          onClose={() => setOpenId(null)}
        />
      )}
    </>
  );
}

function PersonPaymentsModal({ person, pw, onClose }) {
  const [state, setState] = useState({ loading: true, data: null, err: "" });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/people/${person.id}/payments`,
          { headers: { "X-Admin-Password": pw } },
        );
        if (!cancelled) setState({ loading: false, data: r.data, err: "" });
      } catch (e) {
        if (!cancelled) setState({
          loading: false, data: null,
          err: e?.response?.data?.detail || e.message,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [person.id, pw]);
  const money = (a, c) =>
    new Intl.NumberFormat("en-AU", { style: "currency", currency: c || "AUD" }).format(a || 0);
  return (
    <div
      className="fixed inset-0 z-50 bg-[#0D2F4D]/40 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="person-payments-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl border border-[#E4E7EA] shadow-xl overflow-hidden"
      >
        <div className="flex items-start justify-between px-6 py-5 border-b border-[#E4E7EA]">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-semibold">Payments received</p>
            <h3 className="font-display text-2xl font-extrabold text-[#0D2F4D] mt-1" data-testid="person-payments-name">
              {person.first_name} {person.last_name}
            </h3>
            <p className="text-xs text-[#6F8FA8]">{person.email}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="person-payments-close"
            className="text-[#6F8FA8] hover:text-[#0D2F4D] transition-colors p-1"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-6 py-5">
          {state.loading && <p className="text-[#6F8FA8] text-sm">Loading…</p>}
          {state.err && <p className="text-red-600 text-sm">Couldn't load: {state.err}</p>}
          {state.data && (
            <>
              <div className="flex items-baseline justify-between mb-4">
                <p className="text-xs text-[#6F8FA8] uppercase tracking-widest">
                  {state.data.count} payment{state.data.count === 1 ? "" : "s"}
                </p>
                <p className="font-display text-2xl font-extrabold text-[#0D2F4D]" data-testid="person-payments-total">
                  {money(state.data.total_amount, state.data.currency)}
                </p>
              </div>
              {state.data.count === 0 ? (
                <p className="text-[#6F8FA8] text-sm py-6 text-center">
                  No payments received from this person yet.
                </p>
              ) : (
                <ul className="divide-y divide-[#F2F2EF]" data-testid="person-payments-list">
                  {state.data.payments.map((p) => (
                    <li key={p.id} className="py-3 flex items-start justify-between gap-4">
                      <div>
                        <p className="text-[#0D2F4D] font-semibold text-sm">
                          {p.product_name || "Payment"}
                        </p>
                        <p className="text-xs text-[#6F8FA8]">
                          {p.source} · {new Date(p.occurred_at || p.received_at).toLocaleDateString("en-AU", { year: "numeric", month: "short", day: "numeric" })}
                          {p.external_transaction_id ? ` · ${p.external_transaction_id.slice(0, 20)}` : ""}
                        </p>
                      </div>
                      <p className="font-display font-bold text-[#0D2F4D] whitespace-nowrap">
                        {money(p.amount, p.currency)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
